import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import {
    legacyTextToMessageDoc,
    messageDocHasPrepMention,
    messageDocToPlainText,
    mentionCandidates,
    prependMentionToMessageDoc,
    stringifyMessageDoc,
    updateMentionCandidates,
} from "components/atoms/message";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import useToast from "components/atoms/toast/useToast";
import ManuscriptEditorPanel from "components/organisms/manuscript/editor/ManuscriptEditorPanel";
import ManuscriptEditorPageHeader, {
    MANUSCRIPT_HEADER_HEIGHT,
    normalizePresenceParticipant,
    useManuscriptTitleEditor,
} from "components/organisms/manuscript/header/ManuscriptHeader";
import { useManuscriptHeaderMenus } from "components/organisms/manuscript/header/useManuscriptHeaderMenus";
import ManuscriptLeftPanel from "components/organisms/manuscript/ManuscriptLeftPanel";
import ManuscriptEditorPageLayout from "components/organisms/manuscript/ManuscriptEditorPageLayout";
import ManuscriptRightPanel from "components/organisms/manuscript/ManuscriptRightPanel";
import ManuscriptRightRail from "components/organisms/manuscript/ManuscriptRightRail";
import ManuscriptShortcutModal from "components/organisms/manuscript/help/ManuscriptShortcutModal";
import ManuscriptSettingsModal, {
    useManuscriptSettings,
} from "components/organisms/manuscript/settings/ManuscriptSettingsModal";
import ManuscriptAiEditModal from "components/organisms/manuscript/editor/ai/edit/ManuscriptAiEditModal";
import ManuscriptReviewThreadOverlay from "components/organisms/manuscript/review/ManuscriptReviewThreadOverlay";
import { useManuscriptReviewAIWebSocket } from "components/organisms/manuscript/review/useManuscriptReviewAIWebSocket";
import ManuscriptNodeNameModal from "components/page/modal/ManuscriptNodeNameModal";
import { getBlockingAIEditError } from "components/organisms/manuscript/editor/ai/edit/guards";
import { useAIEditWebSocket } from "components/organisms/manuscript/editor/ai/edit/useAIEditWebSocket";
import ManuscriptLatexErrorFixModal from "components/organisms/manuscript/editor/ai/latex-error-fix/ManuscriptLatexErrorFixModal";
import { useLatexErrorFixWebSocket } from "components/organisms/manuscript/editor/ai/latex-error-fix/useLatexErrorFixWebSocket";
import ManuscriptTemplateOverlay, {
    isTemplateGenerationInProgress,
} from "components/organisms/manuscript/editor/ai/template/ManuscriptTemplateOverlay";
import {
    buildCompileLogAnnotationsByNodeId,
    normalizeCompileLogPath,
    parseCompileLog,
} from "components/organisms/manuscript/pdf/compile-log";
import {
    useLatestManuscriptCompile,
    useLatestManuscriptJob,
    useLatestSuccessfulManuscriptCompile,
    useManuscript,
    useManuscriptCompileCreate,
    useManuscriptDocument,
    useManuscriptHistory,
    useManuscriptHistoryRestore,
    useManuscriptReviewCreate,
    useManuscriptReviewMessageCreate,
    useManuscriptReviewReopen,
    useManuscriptReviewResolve,
    useManuscriptReviews,
    useManuscriptTree,
} from "hooks/queries/useManuscript";
import { useSpace, useUser } from "hooks/queries/useUser";
import { useManuscriptOTClient } from "hooks/useManuscriptOTClient";
import { useManuscriptEvents } from "hooks/useManuscriptEvents";
import { realtime } from "components/organisms/editor/history/realtime";
import { historyOT } from "components/organisms/manuscript/history/history";
import {
    createEmptySelectionActionSelection,
    createSelectionActionMenuItems,
    createSelectionActionMenuState,
} from "components/organisms/manuscript/editor/selection-actions";
import {
    buildReviewOverlayPosition,
    createReviewOverlayClosedState,
    getReviewThreadAnchorRect,
    resolveReviewAnchor,
    resolveReviewThreadSelection,
    syncReviewThreadCommentCreated,
    syncReviewThreadCommentResolved,
    shouldDismissReviewOverlayOnSelectionChange,
} from "components/organisms/manuscript/review/review";
import { useMissingGraphicsTreeRefetch } from "components/organisms/manuscript/editor/visual/figure/graphics-preview";
import {
    downloadManuscriptSourceZip,
    patchManuscript,
    deleteManuscriptNode,
    getManuscriptSearch,
    patchManuscriptSettings,
    patchManuscriptNode,
    postManuscriptNode,
    postManuscriptFigureUrl,
} from "utils/request/manuscript";
import { getSpaceMemberProfiles } from "utils/request/space";
import { isPaidPlanUser } from "utils/user";
import SpaceDetailPageLayout from "components/templates/SpaceDetailPageLayout";

const HEADER_HEIGHT = MANUSCRIPT_HEADER_HEIGHT;
const ENABLE_MANUSCRIPT_TRACK_CHANGES = false;
const TEXT_FILE_EXTENSIONS = new Set([
    "bib",
    "bst",
    "cls",
    "csv",
    "json",
    "md",
    "py",
    "r",
    "rb",
    "sh",
    "sty",
    "svg",
    "tex",
    "toml",
    "tsv",
    "txt",
    "xml",
    "yaml",
    "yml",
]);
const TEXT_MIME_TYPES = new Set([
    "application/javascript",
    "application/json",
    "application/ld+json",
    "application/sql",
    "application/typescript",
    "application/x-latex",
    "application/x-tex",
    "application/x-yaml",
    "application/xml",
    "image/svg+xml",
]);

function buildCompileRevisionMap(historyItems) {
    const items = Array.isArray(historyItems) ? historyItems : [];
    return items.reduce((map, item) => {
        if (item?.revision_type !== "compile_requested") {
            return map;
        }

        const jobId = item?.payload?.meta?.job_id;
        if (jobId == null) {
            return map;
        }

        map.set(Number(jobId), item?.revision_seq ?? null);
        return map;
    }, new Map());
}

function normalizeHistoryItems(historyData) {
    if (Array.isArray(historyData)) {
        return historyData;
    }
    if (Array.isArray(historyData?.results)) {
        return historyData.results;
    }
    return [];
}

const getFileExtension = (name = "") => {
    const parts = String(name).toLowerCase().split(".");
    return parts.length > 1 ? parts.pop() : "";
};

const isTextUploadFile = (file) => {
    const contentType = (file?.type || "").toLowerCase();
    if (contentType.startsWith("text/")) {
        return true;
    }
    if (TEXT_MIME_TYPES.has(contentType)) {
        return true;
    }
    return TEXT_FILE_EXTENSIONS.has(getFileExtension(file?.name));
};

const PageLayout = styled.div`
    width: 100%;
    height: 100vh;
    overflow: hidden;
    background: var(--color-Background);
`;

const LeftRailNavButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid ${(props) => (props.$active ? "var(--color-Key)" : "transparent")};
    border-radius: 8px;
    background: ${(props) => (props.$active ? "color-mix(in srgb, var(--color-Key) 12%, white)" : "transparent")};
    cursor: pointer;

    &:hover {
        background: ${(props) =>
            props.$active ? "color-mix(in srgb, var(--color-Key) 12%, white)" : "var(--color-ButtonHover4)"};
    }
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 32px;
    padding: 0 10px;
    border: 1px solid ${(props) => (props.$primary ? "var(--color-Key)" : "var(--color-Line)")};
    border-radius: 6px;
    background: ${(props) => (props.$primary ? "var(--color-Key)" : "var(--color-White)")};
    color: ${(props) => (props.$primary ? "var(--color-White)" : "var(--color-NormalBlack)")};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: ${(props) => (props.$primary ? "#10765d" : "var(--color-Base1)")};
    }
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const TreeActionMenu = styled.div`
    position: fixed;
    top: ${(props) => `${props.$top}px`};
    left: ${(props) => `${props.$left}px`};
    min-width: 124px;
    padding: 4px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-White);
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
    z-index: 20;
`;

const TreeActionMenuItem = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    height: 30px;
    padding: 0 8px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: var(--color-NormalBlack);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: var(--color-Base1);
    }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 80;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.35);
`;

const Dialog = styled.div`
    width: min(520px, calc(100vw - 32px));
    max-height: calc(100vh - 32px);
    overflow: auto;
    border: 1px solid var(--color-Line);
    border-radius: 12px;
    background: var(--color-White);
    box-shadow: 0 20px 36px rgba(15, 23, 42, 0.2);
`;

const DialogHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 44px;
    padding: 0 14px;
    border-bottom: 1px solid var(--color-Line);
    font-size: 14px;
    font-weight: 700;
    color: var(--color-NormalBlack);
`;

const DialogBody = styled.div`
    padding: 10px;
`;

const ConflictMessage = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px 0 12px 0;
    color: var(--color-NormalBlack);
    font-size: 13px;
    line-height: 1.5;
`;

const ConflictApplyRow = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding-top: 2px;
    color: var(--color-NormalBlack);
    font-size: 12px;
    font-weight: 600;
`;

const EditorContextMenu = styled.div`
    position: fixed;
    z-index: 80;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px;
    border: 1px solid var(--color-Line);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
    backdrop-filter: blur(8px);
`;

const EditorContextMenuItem = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--color-NormalBlack);
    cursor: pointer;

    &:hover {
        background: var(--color-Base1);
    }
`;

const EditorContextMenuDivider = styled.div`
    width: 1px;
    height: 18px;
    margin: 0 2px;
    background: var(--color-Line);
    border-radius: 999px;
`;

const EditorContextMenuTooltipWrap = styled.div`
    position: relative;
    display: inline-flex;
`;

const ContextMenuToolTip = styled.div`
    position: absolute;
    left: 50%;
    bottom: calc(100% + 8px);
    transform: translateX(-50%);
    display: inline-flex;
    padding: 4px 6px;
    width: max-content;
    max-width: 300px;
    background-color: rgba(11, 33, 44, 0.9);
    border-radius: 4px;
    letter-spacing: -0.25px;
    text-align: center;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.4;
    color: var(--color-White);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
        opacity 120ms ease,
        visibility 120ms ease;
    z-index: 1;

    ${EditorContextMenuTooltipWrap}:hover &,
    ${EditorContextMenuTooltipWrap}:focus-within & {
        opacity: 1;
        visibility: visible;
    }
`;

function buildTree(nodes) {
    const nodeMap = new Map((nodes || []).map((node) => [node.id, { ...node, children: [] }]));
    const roots = [];
    nodeMap.forEach((node) => {
        if (node.parent_id && nodeMap.has(node.parent_id)) {
            nodeMap.get(node.parent_id).children.push(node);
        } else {
            roots.push(node);
        }
    });
    const sortNodes = (items) => {
        items.sort((left, right) => {
            if (left.node_type === "folder" && right.node_type !== "folder") return -1;
            if (left.node_type !== "folder" && right.node_type === "folder") return 1;
            return left.name.localeCompare(right.name);
        });
        items.forEach((item) => sortNodes(item.children));
    };
    sortNodes(roots);
    return { roots, nodeMap };
}

function ancestorFolderIds(node, nodeMap) {
    const ids = [];
    let current = node?.parent_id ? nodeMap.get(node.parent_id) : null;
    while (current) {
        ids.push(current.id);
        current = current.parent_id ? nodeMap.get(current.parent_id) : null;
    }
    return ids;
}

function caretInfo(text, position) {
    const lines = (text || "").slice(0, Math.max(0, position || 0)).split("\n");
    return {
        line: lines.length,
        column: lines[lines.length - 1].length + 1,
    };
}

const OUTLINE_COMMAND_LEVELS = {
    part: 0,
    chapter: 0,
    section: 1,
    subsection: 2,
    subsubsection: 3,
    paragraph: 4,
    subparagraph: 5,
};

function extractLatexOutline(content) {
    const lines = (content || "").split("\n");
    const roots = [];
    const stack = [];

    lines.forEach((line, index) => {
        const match = line.match(
            /\\(part|chapter|section|subsection|subsubsection|paragraph|subparagraph)\*?\{([^}]*)\}/,
        );
        if (!match) return;
        const [, command, title] = match;
        const item = {
            id: `${command}-${index + 1}`,
            command,
            title: title.trim() || command,
            line: index + 1,
            level: OUTLINE_COMMAND_LEVELS[command] ?? 1,
            children: [],
        };

        while (stack.length && stack[stack.length - 1].level >= item.level) {
            stack.pop();
        }

        if (stack.length) {
            stack[stack.length - 1].children.push(item);
        } else {
            roots.push(item);
        }

        stack.push(item);
    });

    return roots;
}

function findActiveOutlinePath(items, line, path = []) {
    let bestCandidate = {
        path: [],
        line: -1,
    };

    items.forEach((item) => {
        if (item.line > line) return;
        const nextPath = [...path, item];
        const childCandidate = findActiveOutlinePath(item.children || [], line, nextPath);
        const candidate = childCandidate.line >= 0 ? childCandidate : { path: nextPath, line: item.line };

        if (
            candidate.line > bestCandidate.line ||
            (candidate.line === bestCandidate.line && candidate.path.length > bestCandidate.path.length)
        ) {
            bestCandidate = candidate;
        }
    });

    return bestCandidate;
}

function getThreadCreationAnchorRange(thread) {
    return thread?.creation_anchor_range || null;
}

function getThreadAnchorPath(thread) {
    return thread?.anchor_path || thread?.anchor_file?.path_cache || "";
}

function normalizeReviewThread(thread) {
    if (!thread) {
        return thread;
    }
    const creationAnchorRange = getThreadCreationAnchorRange(thread);
    return {
        ...thread,
        id: thread.id == null ? thread.id : String(thread.id),
        creation_anchor_range: creationAnchorRange,
        anchor_path: getThreadAnchorPath(thread),
    };
}

function getReviewThreadRange(thread) {
    const anchor = getThreadCreationAnchorRange(thread) || {};
    if (!anchor.start_line || !anchor.start_col || !anchor.end_col) {
        return null;
    }
    return {
        line: anchor.start_line,
        startCol: anchor.start_col,
        endCol: anchor.end_col,
    };
}

function formatSelectionLocation(selection) {
    if (!selection?.hasSelection || !selection?.startLine || !selection?.startColumn) {
        return "";
    }

    const start = `L${selection.startLine}:${selection.startColumn}`;
    const endLine = selection.endLine || selection.startLine;
    const endColumn = selection.endColumn || selection.startColumn;
    const end = `L${endLine}:${endColumn}`;
    return `${start}-${end}`;
}

function useLatest(value) {
    const ref = useRef(value);

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref;
}

function ManuscriptEditorPage() {
    const { setToast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { space_uuid, manuscriptId } = useParams();
    const [searchParams] = useSearchParams();
    const spaceMenuFoldRef = useRef(null);

    const userQuery = useUser();
    const manuscriptQuery = useManuscript(manuscriptId);
    const spaceQuery = useSpace();
    const treeQuery = useManuscriptTree(manuscriptId);
    const latestCompileQuery = useLatestManuscriptCompile(manuscriptId, {
        refetchInterval: (data) => {
            if (!data) {
                return false;
            }
            return ["queued", "running"].includes(data.status) ? 2500 : false;
        },
    });
    const latestSuccessfulCompileQuery = useLatestSuccessfulManuscriptCompile(manuscriptId);
    const refetchLatestSuccessfulCompile = latestSuccessfulCompileQuery.refetch;
    const latestTemplateGenerationJobQuery = useLatestManuscriptJob(
        manuscriptId,
        { job_type: "template_generation" },
        {
            refetchInterval: (data) => {
                if (!data) {
                    return false;
                }
                return ["queued", "running"].includes(data.status) ? 2500 : false;
            },
        },
    );
    const compileMutation = useManuscriptCompileCreate();
    const renameManuscriptMutation = useMutation((payload) => patchManuscript(space_uuid, payload), {
        onSuccess: (data) => {
            queryClient.setQueryData(["manuscript", space_uuid, manuscriptId], data);
            queryClient.invalidateQueries(["manuscripts", space_uuid]);
        },
    });
    const historyQuery = useManuscriptHistory(manuscriptId);
    const reviewQuery = useManuscriptReviews(manuscriptId);
    const refetchReviews = reviewQuery.refetch;
    const restoreMutation = useManuscriptHistoryRestore();
    const reviewCreateMutation = useManuscriptReviewCreate();
    const reviewMessageMutation = useManuscriptReviewMessageCreate();
    const reviewResolveMutation = useManuscriptReviewResolve();
    const reviewReopenMutation = useManuscriptReviewReopen();
    const treeNodes = useMemo(() => treeQuery.data || [], [treeQuery.data]);
    const { roots, nodeMap } = useMemo(() => buildTree(treeNodes), [treeNodes]);

    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [expandedFolderIds, setExpandedFolderIds] = useState({});
    const [leftPanelTab, setLeftPanelTab] = useState("files");
    const [rightPanelTab, setRightPanelTab] = useState(() => searchParams.get("panel") || "pdf");
    const [editorContent, setEditorContent] = useState("");
    const [currentEditorMode, setCurrentEditorMode] = useState("visual");
    const [currentVersion, setCurrentVersion] = useState(null);
    const [saveState, setSaveState] = useState("idle");
    const [isDirty, setIsDirty] = useState(false);
    const [selectedReviewThreadId, setSelectedReviewThreadId] = useState(null);
    const [focusedReviewMessageId, setFocusedReviewMessageId] = useState(() => searchParams.get("message"));
    const [pendingReviewThreadId, setPendingReviewThreadId] = useState(() => searchParams.get("thread"));
    const [pendingCreatedReviewThread, setPendingCreatedReviewThread] = useState(null);
    const [highlightedRevisionId] = useState(() => searchParams.get("revision"));
    const [pendingReviewRangeFocus, setPendingReviewRangeFocus] = useState(null);
    const [pendingLintErrorFocus, setPendingLintErrorFocus] = useState(null);
    const [reviewReplyDraft, setReviewReplyDraft] = useState("");
    const [reviewReplyRequestAI, setReviewReplyRequestAI] = useState(false);
    const [reviewComposerDraft, setReviewComposerDraft] = useState("");
    const [reviewComposerRequestAI, setReviewComposerRequestAI] = useState(false);
    const [reviewMentionCandidates, setReviewMentionCandidates] = useState([...mentionCandidates]);
    const [reviewAIMessageContentById, setReviewAIMessageContentById] = useState({});
    const [reviewAIStreamingMessageIds, setReviewAIStreamingMessageIds] = useState(() => new Set());
    const [reviewOverlayPosition, setReviewOverlayPosition] = useState(null);
    const [reviewOverlayTick, setReviewOverlayTick] = useState(0);
    const [reviewComposerAnchor, setReviewComposerAnchor] = useState(null);
    const [selectionActionMenuState, setSelectionActionMenuState] = useState(null);
    const [aiEditSession, setAIEditSession] = useState(null);
    const [aiEditPrompt, setAIEditPrompt] = useState("");
    const [aiEditLocalMessage, setAIEditLocalMessage] = useState("");
    const [aiEditAttemptCount, setAIEditAttemptCount] = useState(0);
    const [aiEditPreviewMode, setAIEditPreviewMode] = useState(false);
    const [latexErrorFixSession, setLatexErrorFixSession] = useState(null);
    const [latexErrorFixLocalMessage, setLatexErrorFixLocalMessage] = useState("");
    const [templateGenerationJobEvent, setTemplateGenerationJobEvent] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [compileLogRaw, setCompileLogRaw] = useState("");
    const [compileLogLoading, setCompileLogLoading] = useState(false);
    const [compileLogLoadError, setCompileLogLoadError] = useState("");
    const [hasLintingError, setHasLintingError] = useState(false);
    const [firstLintError, setFirstLintError] = useState(null);
    const [selection, setSelection] = useState(createEmptySelectionActionSelection());
    const [presenceParticipants, setPresenceParticipants] = useState([]);
    const [remoteCursors, setRemoteCursors] = useState({});
    const [activeEditorLine, setActiveEditorLine] = useState(1);
    const [rightPanelWidth, setRightPanelWidth] = useState(360);
    const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
    const [isShortcutModalOpen, setIsShortcutModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [fileConflictDialog, setFileConflictDialog] = useState(null);
    const [fileConflictApplyAll, setFileConflictApplyAll] = useState(false);
    const [treeActionMenuNodeId, setTreeActionMenuNodeId] = useState(null);
    const [treeActionMenuPosition, setTreeActionMenuPosition] = useState(null);
    const [nodeNameModal, setNodeNameModal] = useState(null);
    const [editorCommand, setEditorCommand] = useState(null);

    const latestContentRef = useRef("");
    const syncedContentRef = useRef("");
    const currentVersionRef = useRef(null);
    const isDirtyRef = useRef(false);
    const fileInputRef = useRef(null);
    const treeActionMenuRef = useRef(null);
    const codeMirrorRef = useRef(null);
    const reviewOverlayRef = useRef(null);
    const fileConflictResolverRef = useRef(null);
    const fileConflictApplyAllActionRef = useRef(null);
    const fileConflictReservedNamesRef = useRef(new Set());
    const reviewThreadAnchorElementMapRef = useRef(new Map());
    const editorReviewContextMenuRef = useRef(null);
    const suppressReviewOverlayDismissRef = useRef(false);
    const deferReviewOverlayUntilScrollRef = useRef(false);
    const searchRunRef = useRef(0);
    const preferredEditorModesRef = useRef({});
    const cursorBroadcastTimerRef = useRef(null);
    const pendingCursorPayloadRef = useRef(null);
    const jumpToLineRef = useRef(null);
    const updateReviewOverlayPositionRef = useRef(null);
    const isMac = useMemo(() => /Mac|iPhone|iPad|iPod/i.test(window.navigator.platform || ""), []);
    const isPlanChecking = userQuery.isLoading || userQuery.isFetching;
    const isPaidPlan = !isPlanChecking && isPaidPlanUser(userQuery.data);
    const requirePaidPlan = useCallback(
        (featureName) => {
            if (isPlanChecking) {
                Alert("info", "유료 기능 알림", "요금제 정보를 확인 중입니다. 잠시 후 다시 시도해주세요.");
                return false;
            }
            if (!isPaidPlan) {
                Alert("info", "유료 기능 알림", `${featureName}은 유료 기능입니다.`);
                return false;
            }
            return true;
        },
        [isPaidPlan, isPlanChecking],
    );

    const selectedNode = selectedNodeId ? nodeMap.get(Number(selectedNodeId)) : null;
    const selectedTextNodeId = selectedNode?.node_type === "text" ? selectedNode.id : null;
    const documentQuery = useManuscriptDocument(manuscriptId, selectedTextNodeId);
    const manuscriptDisplayName = manuscriptQuery.data?.display_name || "논문작성";
    const {
        handleTitleBlur: handleManuscriptTitleBlur,
        handleTitleKeyDown: handleManuscriptTitleKeyDown,
        isTitleEditing: isManuscriptTitleEditing,
        setIsTitleEditing: setIsManuscriptTitleEditing,
        setTitleDraft: setManuscriptTitleDraft,
        titleDraft: manuscriptTitleDraft,
        titleInputRef: manuscriptTitleInputRef,
    } = useManuscriptTitleEditor({
        displayName: manuscriptDisplayName,
        onInvalidTitle: () => {
            Alert("warn", "에러", "원고 이름을 입력해주세요.");
        },
        onRenameError: (error) => {
            Alert("warn", "에러", error.response?.data?.display_name?.[0] || "이름 변경에 실패했습니다.");
        },
        onRenameSuccess: () => {
            setToast("원고 이름을 변경했습니다.", "info");
        },
        renameTitle: (displayName) =>
            renameManuscriptMutation.mutateAsync({
                id: manuscriptId,
                display_name: displayName,
            }),
    });

    const documentOutline = useMemo(() => extractLatexOutline(editorContent), [editorContent]);
    const activeOutlinePath = useMemo(() => {
        const result = findActiveOutlinePath(documentOutline, activeEditorLine);
        return result.path;
    }, [documentOutline, activeEditorLine]);
    const activeOutlineIds = useMemo(() => activeOutlinePath.map((item) => item.id), [activeOutlinePath]);
    const latestCompile = latestCompileQuery.data;
    const latestSuccessfulCompile = latestSuccessfulCompileQuery.data;
    const latestSuccessfulPdfArtifact = latestSuccessfulCompile?.artifacts?.find(
        (artifact) => artifact.artifact_kind === "pdf",
    );
    const logArtifact = latestCompile?.artifacts?.find((artifact) => artifact.artifact_kind === "log");
    const compileLogEntries = useMemo(() => parseCompileLog(compileLogRaw), [compileLogRaw]);
    const compileLogAnnotationsByNodeId = useMemo(
        () => buildCompileLogAnnotationsByNodeId(compileLogEntries, treeNodes),
        [compileLogEntries, treeNodes],
    );
    const historyItems = useMemo(() => normalizeHistoryItems(historyQuery.data), [historyQuery.data]);
    const compileRevisionMap = useMemo(() => buildCompileRevisionMap(historyItems), [historyItems]);
    const latestCompileRevisionSeq =
        latestCompile?.id != null ? (compileRevisionMap.get(Number(latestCompile.id)) ?? null) : null;
    const compileInProgress =
        compileMutation.isLoading || ["queued", "running"].includes(String(latestCompile?.status || ""));
    const compileDisabledReason = !selectedTextNodeId ? "컴파일할 텍스트 문서를 선택하세요." : "";
    const displayedPdfArtifact = latestSuccessfulPdfArtifact || null;
    const displayedPdfRevisionSeq =
        latestSuccessfulCompile?.id != null
            ? (compileRevisionMap.get(Number(latestSuccessfulCompile.id)) ?? null)
            : null;
    const reviewThreads = useMemo(() => {
        const baseThreads = Array.isArray(reviewQuery.data) ? reviewQuery.data.map(normalizeReviewThread) : [];
        const threads =
            pendingCreatedReviewThread &&
            !baseThreads.some((thread) => String(thread.id) === String(pendingCreatedReviewThread.id))
                ? [normalizeReviewThread(pendingCreatedReviewThread), ...baseThreads]
                : baseThreads;
        return threads.map((thread) => ({
            ...thread,
            messages: (thread.messages || []).map((message) => {
                const override = reviewAIMessageContentById[String(message.id)];
                const isStreaming = reviewAIStreamingMessageIds.has(String(message.id));
                if (override == null && !isStreaming) {
                    return message;
                }
                return {
                    ...message,
                    content: override ?? message.content,
                    payload: {
                        ...(message.payload || {}),
                        ai_status: isStreaming ? "running" : message.payload?.ai_status,
                    },
                };
            }),
        }));
    }, [pendingCreatedReviewThread, reviewAIMessageContentById, reviewAIStreamingMessageIds, reviewQuery.data]);
    const currentReviewAnchor = resolveReviewAnchor({
        selectedNode,
        selection,
        editorView: codeMirrorRef.current?.view,
    });
    const selectedReviewThread = useMemo(
        () => reviewThreads.find((thread) => thread.id === selectedReviewThreadId) || null,
        [reviewThreads, selectedReviewThreadId],
    );
    const selectedReviewThreadDismissAnchor = useMemo(() => {
        if (!selectedReviewThread) {
            return null;
        }
        const range = selectedReviewThread.creation_anchor_range || {};
        return {
            id: selectedReviewThread.id,
            anchor_file_id: selectedReviewThread.anchor_file_id,
            creation_anchor_range: {
                start_line: range.start_line,
                start_col: range.start_col,
                end_line: range.end_line,
                end_col: range.end_col,
            },
        };
    }, [selectedReviewThread]);

    useEffect(() => {
        if (!pendingCreatedReviewThread?.id || !Array.isArray(reviewQuery.data)) {
            return;
        }
        if (reviewQuery.data.some((thread) => String(thread.id) === String(pendingCreatedReviewThread.id))) {
            setPendingCreatedReviewThread(null);
        }
    }, [pendingCreatedReviewThread, reviewQuery.data]);
    const mySpaceMemberId = useMemo(() => {
        const spaces = Array.isArray(spaceQuery.data) ? spaceQuery.data : [];
        return spaces.find((space) => space.uuid === space_uuid)?.my_member_info?.id ?? null;
    }, [spaceQuery.data, space_uuid]);
    const activePresenceParticipants = useMemo(
        () => presenceParticipants.filter((participant) => participant?.space_member_id || participant?.user_id),
        [presenceParticipants],
    );
    const activeRemoteCursors = useMemo(
        () =>
            Object.values(remoteCursors).filter(
                (cursor) =>
                    cursor &&
                    Number(cursor.node_id) === Number(selectedTextNodeId) &&
                    cursor.participant?.space_member_id !== mySpaceMemberId,
            ),
        [mySpaceMemberId, remoteCursors, selectedTextNodeId],
    );

    const refetchTreeForMissingGraphics = useMissingGraphicsTreeRefetch({
        treeNodes,
        refetchTree: treeQuery.refetch,
    });

    const syncDocumentState = (document, force = false) => {
        if (!document) return;
        const content = document.current_content || "";
        const availableModes = document.available_editor_modes || ["code"];
        const preferredMode = preferredEditorModesRef.current[document.node_id];
        const defaultMode = availableModes.includes("visual")
            ? "visual"
            : document.editor_mode || availableModes[0] || "code";
        const nextEditorMode = preferredMode && availableModes.includes(preferredMode) ? preferredMode : defaultMode;
        syncedContentRef.current = content;
        currentVersionRef.current = document.current_version;
        setCurrentEditorMode(nextEditorMode);
        setCurrentVersion(document.current_version);
        if (force || !isDirtyRef.current) {
            latestContentRef.current = content;
            setEditorContent(content);
            setIsDirty(false);
            isDirtyRef.current = false;
            setSaveState("saved");
            return;
        }
        const dirty = latestContentRef.current !== syncedContentRef.current;
        setIsDirty(dirty);
        isDirtyRef.current = dirty;
        setSaveState(dirty ? "dirty" : "saved");
    };

    const nodeMutation = useMutation(
        async ({ type, nodeId, payload }) => {
            if (type === "create") return postManuscriptNode(space_uuid, manuscriptId, payload);
            if (type === "update") return patchManuscriptNode(space_uuid, manuscriptId, nodeId, payload);
            return deleteManuscriptNode(space_uuid, manuscriptId, nodeId);
        },
        {
            onSuccess: () => {
                treeQuery.refetch();
                manuscriptQuery.refetch();
            },
        },
    );

    const manuscriptSettingsMutation = useMutation(
        (payload) => patchManuscriptSettings(space_uuid, manuscriptId, payload),
        {
            onSuccess: (data) => {
                queryClient.setQueryData(["manuscript", space_uuid, manuscriptId], data);
                manuscriptQuery.refetch();
            },
        },
    );
    const {
        compilerOptions,
        rootCandidateNodes,
        saveProjectSettings,
        setSettingsCompiler,
        setSettingsRootNodeId,
        setSettingsTab,
        settingsCompiler,
        settingsRootNodeId,
        settingsTab,
    } = useManuscriptSettings({
        manuscript: manuscriptQuery.data,
        manuscriptSettingsMutation,
        onError: (error) => {
            Alert("warn", "에러", error.response?.data?.detail || "설정 저장에 실패했습니다.");
        },
        onSaved: () => {
            setToast("프로젝트 설정을 저장했습니다.", "info");
            setIsSettingsModalOpen(false);
        },
        treeNodes,
        treeQuery,
    });

    useEffect(() => {
        currentVersionRef.current = currentVersion;
    }, [currentVersion]);

    useEffect(() => {
        let canceled = false;
        if (!space_uuid) {
            setReviewMentionCandidates([...mentionCandidates]);
            return () => {
                canceled = true;
            };
        }

        getSpaceMemberProfiles(space_uuid)
            .then((profiles) => {
                if (canceled) return;
                updateMentionCandidates(profiles);
                setReviewMentionCandidates([...mentionCandidates]);
            })
            .catch(() => {
                if (canceled) return;
                setReviewMentionCandidates([...mentionCandidates]);
            });

        return () => {
            canceled = true;
        };
    }, [space_uuid]);

    const {
        currentDoc,
        isConnected: isOTConnected,
        isReady: isOTReady,
        sendCursorUpdate,
    } = useManuscriptOTClient({
        spaceUuid: space_uuid,
        manuscriptId,
        nodeId: selectedTextNodeId,
        enabled: !!selectedTextNodeId,
        initialDocument: documentQuery.data,
        trackChangesUserId: ENABLE_MANUSCRIPT_TRACK_CHANGES && mySpaceMemberId ? String(mySpaceMemberId) : null,
        onDocumentState: (document) => {
            if (document?.node_id !== selectedTextNodeId) return;
            queryClient.setQueryData(["manuscriptDocument", space_uuid, manuscriptId, document.node_id], document);
            refetchTreeForMissingGraphics(document);
            syncDocumentState(document, currentVersionRef.current == null);
        },
        onPresenceEvent: (payload) => {
            if ((payload?.event || "") !== "snapshot") {
                return;
            }
            const participants = Array.isArray(payload?.participants)
                ? payload.participants.map(normalizePresenceParticipant).filter(Boolean)
                : [];
            setPresenceParticipants(participants);
            setRemoteCursors((current) => {
                const activeIds = new Set(
                    participants.map((participant) => String(participant.space_member_id || participant.user_id || "")),
                );
                return Object.fromEntries(Object.entries(current).filter(([key]) => activeIds.has(String(key))));
            });
        },
        onCursorEvent: ({ payload, user_id: userId }) => {
            const participant = normalizePresenceParticipant(payload?.participant || { user_id: userId });
            const participantId = participant?.space_member_id || participant?.user_id;
            if (!participantId) {
                return;
            }
            setRemoteCursors((current) => ({
                ...current,
                [participantId]: {
                    ...payload,
                    participant,
                    color: participant.color,
                },
            }));
        },
        onSocketError: (error) => {
            console.error(error);
            setSaveState("error");
        },
    });

    const { isConnected } = useManuscriptEvents({
        spaceUuid: space_uuid,
        manuscriptId,
        enabled: !!manuscriptId,
        isOTConnected,
        requiresOTConnection: !!selectedTextNodeId,
        onManuscriptChanged: (payload) => {
            if (payload?.event === "job_changed" && payload.job?.job_type === "template_generation") {
                setTemplateGenerationJobEvent(payload.job);
                if (["succeeded", "failed"].includes(payload.job.status)) {
                    latestTemplateGenerationJobQuery.refetch();
                    treeQuery.refetch();
                    manuscriptQuery.refetch();
                    historyQuery.refetch();
                    documentQuery.refetch();
                }
                return;
            }
            if (payload?.event === "tree_changed" || payload?.event === "settings_changed") {
                treeQuery.refetch();
                manuscriptQuery.refetch();
                historyQuery.refetch();
            }
        },
    });

    useEffect(() => {
        isDirtyRef.current = isDirty;
    }, [isDirty]);

    useEffect(() => {
        setSelection(createEmptySelectionActionSelection());
        setActiveEditorLine(1);
        setPresenceParticipants([]);
        setRemoteCursors({});
        setHasLintingError(false);
        setFirstLintError(null);
        setPendingLintErrorFocus(null);
        pendingCursorPayloadRef.current = null;
        if (cursorBroadcastTimerRef.current) {
            window.clearTimeout(cursorBroadcastTimerRef.current);
            cursorBroadcastTimerRef.current = null;
        }
    }, [selectedTextNodeId]);

    useEffect(() => {
        setTemplateGenerationJobEvent(null);
        setReviewAIMessageContentById({});
        setReviewAIStreamingMessageIds(new Set());
    }, [manuscriptId]);

    useEffect(() => {
        return () => {
            if (cursorBroadcastTimerRef.current) {
                window.clearTimeout(cursorBroadcastTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleEditorLint = (event) => {
            setHasLintingError(Boolean(event?.detail?.hasLintingError));
            setFirstLintError(event?.detail?.firstError || null);
        };

        window.addEventListener("editor:lint", handleEditorLint);
        return () => {
            window.removeEventListener("editor:lint", handleEditorLint);
        };
    }, []);

    useEffect(() => {
        if (!reviewThreads.length) {
            setSelectedReviewThreadId(null);
            return;
        }
        if (selectedReviewThreadId == null) {
            return;
        }
        const exists = reviewThreads.some((thread) => thread.id === selectedReviewThreadId);
        if (!exists) {
            setSelectedReviewThreadId(null);
        }
    }, [reviewThreads, selectedReviewThreadId]);

    useEffect(() => {
        setReviewReplyDraft("");
    }, [selectedReviewThreadId]);

    useEffect(() => {
        setReviewComposerDraft("");
        setReviewComposerRequestAI(false);
    }, [
        reviewComposerAnchor?.anchor_file_id,
        reviewComposerAnchor?.creation_anchor_range?.start_line,
        reviewComposerAnchor?.creation_anchor_range?.start_col,
        reviewComposerAnchor?.creation_anchor_range?.end_line,
        reviewComposerAnchor?.creation_anchor_range?.end_col,
    ]);

    useEffect(() => {
        if (suppressReviewOverlayDismissRef.current) {
            suppressReviewOverlayDismissRef.current = false;
            return;
        }

        const shouldDismiss = shouldDismissReviewOverlayOnSelectionChange({
            selectedThread: selectedReviewThreadDismissAnchor,
            selectedNodeId: selectedTextNodeId,
            selection,
        });
        if (shouldDismiss) {
            const closedState = createReviewOverlayClosedState();
            setSelectedReviewThreadId(closedState.selectedReviewThreadId);
            setReviewReplyDraft(closedState.reviewReplyDraft);
            setReviewOverlayPosition(closedState.reviewOverlayPosition);
        }
    }, [selectedReviewThreadDismissAnchor, selectedTextNodeId, selection]);

    useEffect(() => {
        if (!reviewComposerAnchor) {
            return;
        }

        const currentOp = currentReviewAnchor?.creation_anchor_range?.op || null;
        const composerOp = reviewComposerAnchor?.creation_anchor_range?.op || null;
        const sameSelection =
            currentReviewAnchor?.anchor_file_id != null &&
            Number(currentReviewAnchor.anchor_file_id) === Number(reviewComposerAnchor.anchor_file_id) &&
            JSON.stringify(currentOp) === JSON.stringify(composerOp);

        if (!sameSelection) {
            setReviewComposerAnchor(null);
            setReviewComposerDraft("");
            setReviewComposerRequestAI(false);
        }
    }, [currentReviewAnchor, reviewComposerAnchor]);

    useEffect(() => {
        if (!pendingReviewRangeFocus) return;
        if (!selectedTextNodeId) return;
        if (Number(selectedTextNodeId) !== Number(pendingReviewRangeFocus.nodeId)) return;
        if (!documentQuery.data || Number(documentQuery.data.node_id) !== Number(pendingReviewRangeFocus.nodeId))
            return;

        jumpToLineRef.current?.(
            pendingReviewRangeFocus.line,
            {
                startCol: pendingReviewRangeFocus.startCol,
                endCol: pendingReviewRangeFocus.endCol,
            },
            { scrollMode: "center" },
        );
        window.requestAnimationFrame(() => {
            deferReviewOverlayUntilScrollRef.current = false;
            setReviewOverlayTick((prev) => prev + 1);
        });
        setPendingReviewRangeFocus(null);
    }, [documentQuery.data, pendingReviewRangeFocus, selectedTextNodeId]);

    useEffect(() => {
        if (!pendingLintErrorFocus) return;
        if (currentEditorMode !== "code") return;
        if (!selectedTextNodeId) return;
        if (Number(selectedTextNodeId) !== Number(pendingLintErrorFocus.nodeId)) return;
        if (!documentQuery.data || Number(documentQuery.data.node_id) !== Number(pendingLintErrorFocus.nodeId)) return;
        if (!codeMirrorRef.current?.view) return;

        jumpToLineRef.current?.(pendingLintErrorFocus.line, {
            startCol: pendingLintErrorFocus.startCol,
            endCol: pendingLintErrorFocus.endCol,
        });
        setPendingLintErrorFocus(null);
    }, [currentEditorMode, documentQuery.data, pendingLintErrorFocus, selectedTextNodeId]);

    useEffect(() => {
        if ((!selectedReviewThread && !reviewComposerAnchor) || rightPanelTab !== "review" || isRightPanelCollapsed) {
            setReviewOverlayPosition(null);
            return;
        }

        if (deferReviewOverlayUntilScrollRef.current) {
            setReviewOverlayPosition(null);
            return;
        }

        updateReviewOverlayPositionRef.current?.();
        const animationFrameId = window.requestAnimationFrame(() => {
            updateReviewOverlayPositionRef.current?.();
        });

        const handleViewportChange = () => {
            updateReviewOverlayPositionRef.current?.();
        };

        window.addEventListener("resize", handleViewportChange);
        window.addEventListener("scroll", handleViewportChange, true);
        return () => {
            window.cancelAnimationFrame(animationFrameId);
            window.removeEventListener("resize", handleViewportChange);
            window.removeEventListener("scroll", handleViewportChange, true);
        };
    }, [
        selectedReviewThread,
        reviewComposerAnchor,
        selectedTextNodeId,
        activeEditorLine,
        rightPanelTab,
        isRightPanelCollapsed,
        treeNodes,
        documentQuery.data,
        reviewOverlayTick,
    ]);

    useEffect(() => {
        if (
            (!selectedReviewThread && !reviewComposerAnchor) ||
            !reviewOverlayRef.current ||
            typeof ResizeObserver === "undefined"
        ) {
            return undefined;
        }

        const observer = new ResizeObserver(() => {
            setReviewOverlayTick((prev) => prev + 1);
        });
        observer.observe(reviewOverlayRef.current);
        return () => observer.disconnect();
    }, [selectedReviewThread, reviewComposerAnchor]);

    useEffect(() => {
        if (!treeNodes.length) return;
        if (manuscriptQuery.isLoading || !manuscriptQuery.data) return;
        if (selectedNodeId && nodeMap.has(Number(selectedNodeId))) return;
        const defaultNode =
            (manuscriptQuery.data?.root_tree_node_id && nodeMap.get(manuscriptQuery.data.root_tree_node_id)) ||
            treeNodes.find((node) => node.node_type === "text") ||
            treeNodes[0];
        if (!defaultNode) return;
        setSelectedNodeId(defaultNode.id);
        const ancestors = ancestorFolderIds(defaultNode, nodeMap);
        if (ancestors.length) {
            setExpandedFolderIds((prev) => {
                const next = { ...prev };
                ancestors.forEach((id) => {
                    next[id] = true;
                });
                return next;
            });
        }
    }, [manuscriptQuery.data, manuscriptQuery.isLoading, nodeMap, selectedNodeId, treeNodes]);

    const {
        isStreaming: isAIEditStreaming,
        status: aiEditStatus,
        responseText: aiEditResponseText,
        errorMessage: aiEditErrorMessage,
        hasResult: aiEditHasResult,
        startRequest: startAIEditRequest,
        stopRequest: stopAIEditRequest,
        reset: resetAIEdit,
    } = useAIEditWebSocket({
        spaceUuid: space_uuid,
        manuscriptId,
        nodeId: selectedTextNodeId,
        enabled: isPaidPlan && !!selectedTextNodeId,
    });

    const {
        isRunning: isLatexErrorFixRunning,
        status: latexErrorFixStatus,
        summary: latexErrorFixSummary,
        replacements: latexErrorFixReplacements,
        errorMessage: latexErrorFixErrorMessage,
        hasResult: latexErrorFixHasResult,
        startRequest: startLatexErrorFixRequest,
        stopRequest: stopLatexErrorFixRequest,
        reset: resetLatexErrorFix,
    } = useLatexErrorFixWebSocket({
        spaceUuid: space_uuid,
        manuscriptId,
        nodeId: selectedTextNodeId,
        enabled: isPaidPlan && !!latexErrorFixSession && !!selectedTextNodeId,
    });

    const handleReviewAIStarted = useCallback((payload) => {
        const messageId = String(payload?.message_id || "");
        if (!messageId) {
            return;
        }
        setReviewAIStreamingMessageIds((prev) => new Set(prev).add(messageId));
        setReviewAIMessageContentById((prev) => ({
            ...prev,
            [messageId]: stringifyMessageDoc(legacyTextToMessageDoc("")),
        }));
    }, []);

    const handleReviewAIDelta = useCallback((payload) => {
        const messageId = String(payload?.message_id || "");
        if (!messageId) {
            return;
        }
        const delta = payload?.content || "";
        if (!delta) {
            return;
        }
        setReviewAIMessageContentById((prev) => {
            const previousText = prev[messageId] ? messageDocToPlainText(prev[messageId]) : "";
            return {
                ...prev,
                [messageId]: stringifyMessageDoc(legacyTextToMessageDoc(`${previousText}${delta}`)),
            };
        });
    }, []);

    const handleReviewAICompleted = useCallback(
        async (payload) => {
            const messageId = String(payload?.message_id || "");
            if (messageId) {
                setReviewAIStreamingMessageIds((prev) => {
                    const next = new Set(prev);
                    next.delete(messageId);
                    return next;
                });
                if (payload?.content != null) {
                    setReviewAIMessageContentById((prev) => ({
                        ...prev,
                        [messageId]: stringifyMessageDoc(legacyTextToMessageDoc(payload.content)),
                    }));
                }
            }
            await refetchReviews();
        },
        [refetchReviews],
    );

    const handleReviewAIStopped = useCallback(
        async (payload) => {
            const messageId = String(payload?.message_id || "");
            if (messageId) {
                setReviewAIStreamingMessageIds((prev) => {
                    const next = new Set(prev);
                    next.delete(messageId);
                    return next;
                });
                if (payload?.content != null) {
                    setReviewAIMessageContentById((prev) => ({
                        ...prev,
                        [messageId]: stringifyMessageDoc(legacyTextToMessageDoc(payload.content)),
                    }));
                }
            }
            await refetchReviews();
        },
        [refetchReviews],
    );

    const handleReviewAIError = useCallback(
        async (payload) => {
            const messageId = String(payload?.message_id || "");
            if (messageId) {
                setReviewAIStreamingMessageIds((prev) => {
                    const next = new Set(prev);
                    next.delete(messageId);
                    return next;
                });
            }
            Alert("warn", "에러", payload?.message || "AI 리뷰 생성에 실패했습니다.");
            await refetchReviews();
        },
        [refetchReviews],
    );

    const { startRequest: startReviewAIRequest } = useManuscriptReviewAIWebSocket({
        spaceUuid: space_uuid,
        manuscriptId,
        enabled: isPaidPlan && !!manuscriptId,
        onStarted: handleReviewAIStarted,
        onDelta: handleReviewAIDelta,
        onCompleted: handleReviewAICompleted,
        onStopped: handleReviewAIStopped,
        onError: handleReviewAIError,
    });

    useEffect(() => {
        if (!documentQuery.data) {
            return;
        }

        const hasLiveOtSession =
            currentDoc &&
            Number(documentQuery.data.node_id) === Number(selectedTextNodeId) &&
            currentDoc.getVersion() != null;

        syncDocumentState(documentQuery.data, !hasLiveOtSession);
    }, [currentDoc, documentQuery.data, selectedTextNodeId]);

    useEffect(() => {
        if (selectedNode?.node_type !== "text") {
            setCurrentEditorMode("code");
        }
    }, [selectedNode]);

    useEffect(() => {
        if (rightPanelTab !== "review" || isRightPanelCollapsed) {
            return;
        }
        refetchReviews();
    }, [isRightPanelCollapsed, refetchReviews, rightPanelTab]);

    useEffect(() => {
        if (latestCompile?.status !== "succeeded") return;
        setRightPanelTab("pdf");
        setIsRightPanelCollapsed(false);
        refetchLatestSuccessfulCompile();
    }, [latestCompile?.id, latestCompile?.status, refetchLatestSuccessfulCompile]);

    useEffect(() => {
        if (!logArtifact?.file_url || ["queued", "running"].includes(latestCompile?.status || "")) {
            setCompileLogRaw("");
            setCompileLogLoadError("");
            setCompileLogLoading(false);
            return undefined;
        }

        const controller = new AbortController();

        const loadCompileLog = async () => {
            setCompileLogLoading(true);
            setCompileLogLoadError("");
            try {
                const response = await fetch(logArtifact.file_url, { signal: controller.signal });
                if (!response.ok) {
                    throw new Error(`LOG fetch failed: ${response.status}`);
                }
                const text = await response.text();
                if (!controller.signal.aborted) {
                    setCompileLogRaw(text);
                }
            } catch (_error) {
                if (!controller.signal.aborted) {
                    setCompileLogRaw("");
                    setCompileLogLoadError("로그를 불러오지 못했습니다.");
                }
            } finally {
                if (!controller.signal.aborted) {
                    setCompileLogLoading(false);
                }
            }
        };

        loadCompileLog();
        return () => controller.abort();
    }, [latestCompile?.id, latestCompile?.status, logArtifact?.file_url]);

    const flushCurrentDocument = async () => {
        if (!selectedTextNodeId) {
            return true;
        }

        if (!currentDoc || !currentDoc.hasBufferedOps()) {
            setSaveState("saved");
            return true;
        }

        try {
            setSaveState("saving");
            currentDoc.flush();
            await new Promise((resolve) => {
                const startedAt = Date.now();

                const poll = () => {
                    if (!currentDoc.hasBufferedOps()) {
                        setSaveState("saved");
                        resolve();
                        return;
                    }

                    if (Date.now() - startedAt >= 1200) {
                        resolve();
                        return;
                    }

                    window.setTimeout(poll, 50);
                };

                poll();
            });
            return true;
        } catch (error) {
            setSaveState("error");
            Alert("warn", "에러", "문서 저장 중 오류가 발생했습니다.");
            return false;
        }
    };

    const otSnapshotText = currentDoc && selectedTextNodeId && isOTReady ? currentDoc.getSnapshot() : null;

    const isRealtimeBootstrapReady = useMemo(() => {
        if (!currentDoc || !selectedTextNodeId || !isOTReady) {
            return false;
        }
        if (typeof otSnapshotText !== "string") {
            return false;
        }
        return editorContent === otSnapshotText;
    }, [currentDoc, editorContent, isOTReady, otSnapshotText, selectedTextNodeId]);

    const realtimeExtension = useMemo(() => {
        if (!currentDoc || !selectedTextNodeId || !isOTReady || !isRealtimeBootstrapReady) {
            return null;
        }

        return [
            realtime({ currentDoc }, (error) => {
                console.error(error);
                setSaveState("error");
            }),
            historyOT(currentDoc),
        ];
    }, [currentDoc, isOTReady, isRealtimeBootstrapReady, selectedTextNodeId]);

    useEffect(() => {
        if (!currentDoc || !selectedTextNodeId) {
            return undefined;
        }

        const timer = window.setInterval(() => {
            const saved = currentDoc.pollSavedStatus();
            setSaveState((current) => {
                if (current === "error") {
                    return current;
                }
                if (saved) {
                    return isDirtyRef.current ? "dirty" : "saved";
                }
                return "saving";
            });
        }, 400);

        return () => window.clearInterval(timer);
    }, [currentDoc, selectedTextNodeId]);

    const openNode = async (node) => {
        if (!node) return;
        if (selectedTextNodeId && selectedTextNodeId !== node.id) {
            await flushCurrentDocument();
        }
        if (node.node_type === "folder") {
            setExpandedFolderIds((prev) => ({ ...prev, [node.id]: !prev[node.id] }));
            setSelectedNodeId(node.id);
            return;
        }
        setSelectedNodeId(node.id);
        const ancestors = ancestorFolderIds(node, nodeMap);
        if (ancestors.length) {
            setExpandedFolderIds((prev) => {
                const next = { ...prev };
                ancestors.forEach((id) => {
                    next[id] = true;
                });
                return next;
            });
        }
    };

    const createNodeWithName = async (nodeType, name) => {
        const parentId = selectedNode?.node_type === "folder" ? selectedNode.id : selectedNode?.parent_id || null;
        try {
            const node = await nodeMutation.mutateAsync({
                type: "create",
                payload: {
                    parent_id: parentId,
                    node_type: nodeType,
                    name,
                    current_content: nodeType === "text" ? "" : undefined,
                },
            });
            if (nodeType === "text") {
                setSelectedNodeId(node.id);
            }
            setNodeNameModal(null);
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.name || "노드 생성 중 오류가 발생했습니다.");
        }
    };

    const createNode = (nodeType) => {
        setNodeNameModal({
            mode: "create",
            nodeType,
            title: nodeType === "folder" ? "새 폴더" : "새 파일",
            initialName: nodeType === "folder" ? "sections" : "section.tex",
            submitText: "생성",
            hint: nodeType === "folder" ? "생성할 폴더 이름을 입력하세요." : "생성할 파일 이름을 입력하세요.",
        });
    };

    const setRootNode = async (node) => {
        if (!node || node.node_type !== "text") return;
        try {
            await manuscriptSettingsMutation.mutateAsync({
                root_tree_node_id: node.id,
                default_compiler: manuscriptQuery.data?.default_compiler || settingsCompiler,
            });
            await treeQuery.refetch();
            setToast("루트 문서를 변경했습니다.", "info");
        } catch (error) {
            Alert(
                "warn",
                "에러",
                error.response?.data?.root_tree_node_id?.[0] ||
                    error.response?.data?.detail ||
                    "루트 문서 설정에 실패했습니다.",
            );
        }
    };

    const renameNodeWithName = async (node, name) => {
        if (!node) return;
        if (!name || name === node.name) {
            setNodeNameModal(null);
            return;
        }
        try {
            await nodeMutation.mutateAsync({
                type: "update",
                nodeId: node.id,
                payload: { name },
            });
            setNodeNameModal(null);
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.name?.[0] || "이름 변경에 실패했습니다.");
        }
    };

    const renameSpecificNode = (node) => {
        if (!node) return;
        setNodeNameModal({
            mode: "rename",
            node,
            title: "이름 바꾸기",
            initialName: node.name,
            submitText: "변경",
            hint: "변경할 이름을 입력하세요.",
        });
    };

    const deleteSpecificNode = async (node) => {
        if (!node) return;
        try {
            await nodeMutation.mutateAsync({
                type: "delete",
                nodeId: node.id,
            });
            if (selectedNodeId === node.id) {
                setSelectedNodeId(null);
            }
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "노드 삭제 중 오류가 발생했습니다.");
        }
    };

    const removeSpecificNode = (node) => {
        if (!node) return;
        Confirm("warn", "삭제", `'${node.name}' 항목을 삭제하시겠습니까?`, "삭제", () => {
            deleteSpecificNode(node);
        });
    };

    const getUploadParentId = (parentIdOverride) => {
        if (parentIdOverride !== undefined) {
            return parentIdOverride;
        }
        return selectedNode?.node_type === "folder" ? selectedNode.id : selectedNode?.parent_id || null;
    };

    const normalizeParentId = (parentId) => (parentId == null ? null : Number(parentId));

    const findNodeConflict = (parentId, name, nodeType, excludeNodeId = null) => {
        const normalizedParentId = normalizeParentId(parentId);
        return treeNodes.find(
            (node) =>
                normalizeParentId(node.parent_id) === normalizedParentId &&
                node.node_type === nodeType &&
                node.name === name &&
                Number(node.id) !== Number(excludeNodeId),
        );
    };

    const fileConflictNameKey = (parentId, nodeType, name) =>
        `${normalizeParentId(parentId) || "root"}:${nodeType}:${name}`;

    const reserveFileConflictName = (parentId, nodeType, name) => {
        fileConflictReservedNamesRef.current.add(fileConflictNameKey(parentId, nodeType, name));
    };

    const hasReservedFileConflictName = (parentId, nodeType, name) =>
        fileConflictReservedNamesRef.current.has(fileConflictNameKey(parentId, nodeType, name));

    const createAutoRenamedFileName = (name, parentId, nodeType, excludeNodeId = null) => {
        const dotIndex = name.lastIndexOf(".");
        const hasExtension = dotIndex > 0;
        const baseName = hasExtension ? name.slice(0, dotIndex) : name;
        const extension = hasExtension ? name.slice(dotIndex) : "";
        let index = 1;
        let nextName = `${baseName} (${index})${extension}`;
        while (
            findNodeConflict(parentId, nextName, nodeType, excludeNodeId) ||
            hasReservedFileConflictName(parentId, nodeType, nextName)
        ) {
            index += 1;
            nextName = `${baseName} (${index})${extension}`;
        }
        return nextName;
    };

    const resetFileConflictBatch = () => {
        fileConflictApplyAllActionRef.current = null;
        fileConflictReservedNamesRef.current = new Set();
    };

    const resolveFileConflict = async ({ name, parentId, operation }) => {
        if (fileConflictApplyAllActionRef.current) {
            return fileConflictApplyAllActionRef.current;
        }

        return new Promise((resolve) => {
            fileConflictResolverRef.current = resolve;
            setFileConflictApplyAll(false);
            setFileConflictDialog({ name, parentId, operation });
        });
    };

    const chooseFileConflictAction = (action) => {
        if (fileConflictApplyAll) {
            fileConflictApplyAllActionRef.current = action;
        }
        fileConflictResolverRef.current?.(action);
        fileConflictResolverRef.current = null;
        setFileConflictDialog(null);
        setFileConflictApplyAll(false);
    };

    const cancelFileConflictDialog = () => {
        fileConflictResolverRef.current?.("cancel");
        fileConflictResolverRef.current = null;
        setFileConflictDialog(null);
        setFileConflictApplyAll(false);
    };

    const createUploadFolder = async (name, parentId) => {
        const existingFolder = findNodeConflict(parentId, name, "folder");
        if (existingFolder) {
            return existingFolder;
        }
        return nodeMutation.mutateAsync({
            type: "create",
            payload: {
                parent_id: parentId,
                node_type: "folder",
                name,
            },
        });
    };

    const uploadBinaryFile = async (file, parentIdOverride, options = {}) => {
        if (!file) return;

        const parentId = getUploadParentId(parentIdOverride);
        const conflictPolicy = options.conflictPolicy || "ask";
        const nodeType = isTextUploadFile(file) ? "text" : "binary";
        let uploadName = file.name;
        const existingFile = findNodeConflict(parentId, uploadName, nodeType);
        if (existingFile) {
            const action =
                conflictPolicy === "rename"
                    ? "rename"
                    : await resolveFileConflict({
                          name: uploadName,
                          parentId,
                          operation: "upload",
                      });
            if (action === "cancel") {
                return { count: 0, cancelled: true };
            }
            if (action === "skip") {
                return { count: 0 };
            }
            if (action === "rename") {
                uploadName = createAutoRenamedFileName(uploadName, parentId, nodeType);
            }
            if (action === "overwrite") {
                await nodeMutation.mutateAsync({
                    type: "delete",
                    nodeId: existingFile.id,
                });
            }
        }
        reserveFileConflictName(parentId, nodeType, uploadName);

        if (nodeType === "text") {
            const currentContent = await file.text();
            const createdNode = await nodeMutation.mutateAsync({
                type: "create",
                payload: {
                    name: uploadName,
                    parent_id: parentId,
                    node_type: "text",
                    current_content: currentContent,
                },
            });
            return { ...createdNode, count: 1 };
        }

        const formData = new FormData();
        formData.append("name", uploadName);
        formData.append("file", file);
        if (parentId) {
            formData.append("parent_id", parentId);
        }
        const createdNode = await nodeMutation.mutateAsync({ type: "create", payload: formData });
        return { ...createdNode, count: 1 };
    };

    const importFigureUrl = async (url, parentIdOverride, options = {}) => {
        const parentId = getUploadParentId(parentIdOverride);
        const createdNode = await postManuscriptFigureUrl(space_uuid, manuscriptId, {
            url,
            parent_id: parentId,
            conflict_policy: options.conflictPolicy || "rename",
        });
        await treeQuery.refetch();
        await manuscriptQuery.refetch();
        return { ...createdNode, count: 1 };
    };

    const uploadBinaryItem = async (item, parentId) => {
        if (!item) {
            return { count: 0 };
        }

        if (item instanceof File) {
            const result = await uploadBinaryFile(item, parentId);
            if (result?.cancelled) return { count: 0, cancelled: true };
            return { count: result?.count || 0 };
        }

        if (item.kind === "file" && item.file) {
            const result = await uploadBinaryFile(item.file, parentId);
            if (result?.cancelled) return { count: 0, cancelled: true };
            return { count: result?.count || 0 };
        }

        if (item.kind === "folder") {
            const folder = await createUploadFolder(item.name, parentId);
            let uploadedCount = 0;
            for (const child of item.children || []) {
                const childResult = await uploadBinaryItem(child, folder.id);
                uploadedCount += childResult?.count || 0;
                if (childResult?.cancelled) {
                    return { count: uploadedCount, cancelled: true };
                }
            }
            return { count: uploadedCount };
        }

        return { count: 0 };
    };

    const uploadBinary = async (source) => {
        const files = Array.isArray(source)
            ? source
            : source?.target?.files
              ? Array.from(source.target.files)
              : source?.dataTransfer?.files
                ? Array.from(source.dataTransfer.files)
                : [];

        if (!files.length) {
            return;
        }

        let uploadedCount = 0;
        let lastError = null;
        const baseParentId = getUploadParentId();
        resetFileConflictBatch();

        try {
            for (const file of files) {
                try {
                    const result = await uploadBinaryItem(file, baseParentId);
                    uploadedCount += result?.count || 0;
                    if (result?.cancelled) {
                        break;
                    }
                } catch (error) {
                    lastError = error;
                }
            }

            if (uploadedCount > 0) {
                setToast(
                    uploadedCount === 1 ? "파일이 업로드되었습니다." : `${uploadedCount}개 항목이 업로드되었습니다.`,
                    "info",
                );
            }

            if (lastError) {
                Alert(
                    "warn",
                    "에러",
                    lastError.response?.data?.file?.[0] ||
                        lastError.response?.data?.detail ||
                        "일부 파일 업로드 중 오류가 발생했습니다.",
                );
            }
        } finally {
            if (source?.target) {
                source.target.value = "";
            }
        }
    };

    const scheduleCursorUpdate = (info) => {
        if (!selectedTextNodeId) {
            return;
        }
        pendingCursorPayloadRef.current = {
            ...info,
            node_id: selectedTextNodeId,
            editor_mode: currentEditorMode,
            ts: Date.now(),
        };
        if (cursorBroadcastTimerRef.current) {
            return;
        }
        cursorBroadcastTimerRef.current = window.setTimeout(() => {
            cursorBroadcastTimerRef.current = null;
            if (pendingCursorPayloadRef.current) {
                sendCursorUpdate(pendingCursorPayloadRef.current);
            }
        }, 80);
    };

    const updateSelection = (value, selectionStart) => {
        const info = caretInfo(value, selectionStart);
        setSelection(info);
        scheduleCursorUpdate(info);
    };

    const updateSelectionInfo = (info) => {
        setSelection(info);
        scheduleCursorUpdate(info);
    };

    const moveEditorPositionToViewportCenter = (editorView, pos) => {
        const coords = editorView?.coordsAtPos?.(pos);
        const container = editorView?.scrollDOM;
        if (!coords || !container) {
            return;
        }
        const containerRect = container.getBoundingClientRect();
        const targetCenter = (coords.top + coords.bottom) / 2;
        const containerCenter = containerRect.top + containerRect.height / 2;
        container.scrollTop += targetCenter - containerCenter;
    };

    const moveReviewCursorInActiveEditor = (thread, explicitPos = null, options = {}) => {
        const editorView = codeMirrorRef.current?.view;
        if (!editorView || !thread) {
            return false;
        }
        const scrollIntoView = options?.scrollIntoView === true;
        const scrollMode = options?.scrollMode || (scrollIntoView ? "nearest" : "none");

        let targetPos = Number.isFinite(explicitPos) ? Number(explicitPos) : null;

        if (!Number.isFinite(targetPos)) {
            const range = getReviewThreadRange(thread);
            if (!range?.line || !range?.startCol) {
                return false;
            }

            const totalLines = editorView.state.doc.lines || 1;
            const lineNumber = Math.max(1, Math.min(totalLines, Number(range.line)));
            const lineInfo = editorView.state.doc.line(lineNumber);
            const lineLength = Math.max(0, lineInfo.to - lineInfo.from);
            const startOffset = Math.max(0, Math.min(lineLength, Number(range.startCol) - 1));
            targetPos = lineInfo.from + startOffset;
        }

        const safePos = Math.max(0, Math.min(editorView.state.doc.length, Number(targetPos)));
        const lineInfo = editorView.state.doc.lineAt(safePos);
        const info = {
            line: lineInfo.number,
            column: safePos - lineInfo.from + 1,
            from: safePos,
            to: safePos,
            hasSelection: false,
            startLine: lineInfo.number,
            startColumn: safePos - lineInfo.from + 1,
            endLine: lineInfo.number,
            endColumn: safePos - lineInfo.from + 1,
            selectedText: "",
        };

        editorView.focus();
        editorView.dispatch({
            selection: { anchor: safePos, head: safePos },
            scrollIntoView: scrollMode !== "none",
        });
        if (scrollMode === "center") {
            moveEditorPositionToViewportCenter(editorView, safePos);
        }
        updateSelectionInfo(info);
        updateActiveEditorLine(lineInfo.number);
        return true;
    };

    const updateActiveEditorLine = (lineNumber) => {
        setActiveEditorLine(Math.max(1, lineNumber || 1));
    };

    const jumpToLine = (lineNumber, range = null, options = {}) => {
        const editorView = codeMirrorRef.current?.view;
        if (!editorView) return;
        const scrollMode = options?.scrollMode || "nearest";
        const totalLines = editorView.state.doc.lines || 1;
        const clampedLine = Math.max(1, Math.min(lineNumber, totalLines));
        const lineInfo = editorView.state.doc.line(clampedLine);
        const lineLength = Math.max(0, lineInfo.to - lineInfo.from);
        let selectionStart = lineInfo.from;
        let selectionEnd = lineInfo.from;

        if (range?.startCol && range?.endCol) {
            const startOffset = Math.max(0, Math.min(lineLength, range.startCol - 1));
            const endOffset = Math.max(startOffset, Math.min(lineLength, range.endCol - 1));
            selectionStart = lineInfo.from + startOffset;
            selectionEnd = lineInfo.from + endOffset;
        }

        editorView.focus();
        editorView.dispatch({
            selection: { anchor: selectionStart, head: selectionEnd },
            scrollIntoView: scrollMode !== "none",
        });
        if (scrollMode === "center") {
            moveEditorPositionToViewportCenter(editorView, selectionStart);
        }
        updateSelection(editorContent, selectionEnd);
        updateActiveEditorLine(clampedLine);
    };
    jumpToLineRef.current = jumpToLine;

    const onEditorChange = (nextValue, selectionStart = 0) => {
        if (templateGenerationInProgress) {
            return;
        }
        latestContentRef.current = nextValue;
        setEditorContent(nextValue);
        const dirty = nextValue !== syncedContentRef.current;
        setIsDirty(dirty);
        setSaveState(dirty ? "dirty" : "saved");
        updateSelection(nextValue, selectionStart);
    };

    const handleEditorModeChange = (nextMode) => {
        if (!selectedTextNodeId) return;
        const availableModes = documentQuery.data?.available_editor_modes || ["code"];
        if (!availableModes.includes(nextMode)) return;
        preferredEditorModesRef.current[selectedTextNodeId] = nextMode;
        setCurrentEditorMode(nextMode);
    };

    const saveLabel =
        {
            idle: "준비",
            dirty: "편집 중",
            saving: "저장 중",
            saved: "저장됨",
            error: "오류",
        }[saveState] || "준비";
    const templateGenerationJob = templateGenerationJobEvent || latestTemplateGenerationJobQuery.data;
    const templateGenerationInProgress = isTemplateGenerationInProgress(templateGenerationJob);

    const handleCompile = async () => {
        try {
            await flushCurrentDocument();
            await compileMutation.mutateAsync(manuscriptId);
            setToast("compile 작업을 시작했습니다.", "info");
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "compile 실행에 실패했습니다.");
        }
    };

    const handleCompileAction = () => {
        if (!selectedTextNodeId) {
            return;
        }

        if (hasLintingError) {
            if (firstLintError) {
                const line = Math.max(1, Number(firstLintError.row || 0) + 1);
                const startCol = Math.max(1, Number(firstLintError.column || 0) + 1);
                const endCol = Math.max(startCol, Number(firstLintError.endColumn || firstLintError.column || 0) + 1);

                setPendingLintErrorFocus({
                    nodeId: selectedTextNodeId,
                    line,
                    startCol,
                    endCol,
                });
            }
            handleEditorModeChange("code");
            setRightPanelTab("pdf");
            return;
        }

        handleCompile();
    };

    const handleDownloadSourceZip = async () => {
        try {
            const response = await downloadManuscriptSourceZip(space_uuid, manuscriptId);
            const disposition = response.headers?.["content-disposition"] || "";
            const utf8NameMatch = disposition.match(/filename\\*=UTF-8''([^;]+)/i);
            const plainNameMatch = disposition.match(/filename="?([^";]+)"?/i);
            const fileName = utf8NameMatch
                ? decodeURIComponent(utf8NameMatch[1])
                : plainNameMatch?.[1] || `manuscript-${manuscriptId}.zip`;
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "소스(zip) 다운로드에 실패했습니다.");
        }
    };

    const handleCreateReview = async (content, anchor = reviewComposerAnchor || currentReviewAnchor, options = {}) => {
        try {
            const shouldRequestPrep = Boolean(options?.requestAI);
            const hasPrepMention = messageDocHasPrepMention(content);
            const requestContent =
                shouldRequestPrep && !hasPrepMention
                    ? prependMentionToMessageDoc(content || "", { id: 0, display: "PREP" })
                    : content || "";
            if (shouldRequestPrep && !requirePaidPlan("AI 리뷰")) {
                return false;
            }
            const resolvedAnchor = resolveReviewAnchor({
                anchor,
                selectedNode,
                selection,
                editorView: codeMirrorRef.current?.view,
            });
            if (!resolvedAnchor) {
                throw new Error("리뷰를 생성할 선택 범위를 찾지 못했습니다.");
            }
            const { anchor_file_id, creation_anchor_range } = resolvedAnchor;
            if (creation_anchor_range?.op == null) {
                throw new Error("리뷰 범위의 history-ot 좌표를 찾지 못했습니다.");
            }
            const created = await reviewCreateMutation.mutateAsync({
                manuscriptId,
                anchor_file_id,
                creation_anchor_range,
                content: requestContent,
            });
            syncReviewThreadCommentCreated({
                currentDoc,
                editorView: codeMirrorRef.current?.view,
                threadId: created.id,
                creationAnchorRange: creation_anchor_range,
            });
            suppressReviewOverlayDismissRef.current = true;
            setReviewComposerDraft("");
            setReviewComposerRequestAI(false);
            setSelectedReviewThreadId(created.id);
            setPendingCreatedReviewThread(created);
            setReviewComposerAnchor(null);
            const aiMessage = (created.messages || []).find((message) => message?.payload?.role === "ai");
            if (aiMessage?.id) {
                startReviewAIRequest({
                    threadId: created.id,
                    messageId: aiMessage.id,
                });
            }
            reviewQuery.refetch();
            setToast("리뷰를 생성했습니다.", "info");
            return true;
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || error.message || "리뷰 생성에 실패했습니다.");
            return false;
        }
    };

    const handleReplyReview = async (threadId, content, options = {}) => {
        try {
            const hasPrepMention = messageDocHasPrepMention(content);
            const requestContent =
                Boolean(options?.requestAI) && !hasPrepMention
                    ? prependMentionToMessageDoc(content, { id: 0, display: "PREP" })
                    : content;
            const message = await reviewMessageMutation.mutateAsync({
                manuscriptId,
                threadId,
                content: requestContent,
            });
            setReviewReplyRequestAI(false);
            const aiMessage = message?.ai_message || (message?.payload?.role === "ai" ? message : null);
            if (aiMessage?.id) {
                reviewQuery.refetch().finally(() => {
                    startReviewAIRequest({
                        threadId,
                        messageId: aiMessage.id,
                    });
                });
            } else {
                reviewQuery.refetch();
            }
            setToast("리뷰 답글을 등록했습니다.", "info");
            return true;
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "리뷰 답글 등록에 실패했습니다.");
            return false;
        }
    };

    const handleSelectReviewThread = async (threadId, options = {}) => {
        const selectionResult = resolveReviewThreadSelection({
            currentThreadId: options?.source === "panel" ? null : selectedReviewThreadId,
            nextThreadId: threadId,
            reviewThreads,
            nodeMap,
            getReviewThreadRange,
        });

        if (selectionResult.type === "close") {
            setSelectedReviewThreadId(selectionResult.selectedReviewThreadId);
            setReviewReplyDraft(selectionResult.reviewReplyDraft);
            setReviewOverlayPosition(selectionResult.reviewOverlayPosition);
            return;
        }

        if (selectionResult.type !== "select") {
            return;
        }

        setRightPanelTab("review");
        setIsRightPanelCollapsed(false);
        setSelectedReviewThreadId(selectionResult.selectedReviewThreadId);
        setReviewReplyRequestAI(false);

        const openedFromEditor = options?.source === "editor";
        const openedFromPanel = options?.source === "panel";
        const sameActiveNode = selectionResult.node && Number(selectionResult.node.id) === Number(selectedTextNodeId);

        if (openedFromEditor && sameActiveNode) {
            setSelectionActionMenuState(null);
            moveReviewCursorInActiveEditor(selectionResult.thread, options?.pos, { scrollIntoView: false });
            suppressReviewOverlayDismissRef.current = true;
            setReviewOverlayTick((prev) => prev + 1);
            return;
        }

        if (openedFromPanel && sameActiveNode) {
            setSelectionActionMenuState(null);
            deferReviewOverlayUntilScrollRef.current = true;
            setReviewOverlayPosition(null);
            moveReviewCursorInActiveEditor(selectionResult.thread, null, { scrollMode: "center" });
            suppressReviewOverlayDismissRef.current = true;
            window.requestAnimationFrame(() => {
                deferReviewOverlayUntilScrollRef.current = false;
                setReviewOverlayTick((prev) => prev + 1);
            });
            return;
        }

        if (!selectionResult.node) {
            return;
        }

        if (openedFromPanel) {
            deferReviewOverlayUntilScrollRef.current = true;
            setReviewOverlayPosition(null);
        }
        await openNode(selectionResult.node);

        if (selectionResult.pendingReviewRangeFocus) {
            setPendingReviewRangeFocus(selectionResult.pendingReviewRangeFocus);
        }
    };

    const handleSelectReviewThreadRef = useLatest(handleSelectReviewThread);

    useEffect(() => {
        if (
            !pendingReviewThreadId ||
            !Array.isArray(reviewQuery.data) ||
            !nodeMap.size ||
            !selectedTextNodeId ||
            !documentQuery.data ||
            !codeMirrorRef.current?.view
        )
            return;

        const thread = reviewThreads.find((item) => String(item.id) === String(pendingReviewThreadId));
        if (!thread) {
            setPendingReviewThreadId(null);
            setFocusedReviewMessageId(null);
            return;
        }

        const message = thread.messages?.find((item) => String(item.id) === String(focusedReviewMessageId));
        setFocusedReviewMessageId(message?.id || null);
        handleSelectReviewThreadRef.current(thread.id, { source: "panel" });
        setPendingReviewThreadId(null);
    }, [
        documentQuery.data,
        focusedReviewMessageId,
        handleSelectReviewThreadRef,
        nodeMap,
        pendingReviewThreadId,
        reviewQuery.data,
        reviewThreads,
        selectedTextNodeId,
    ]);

    const handleResolveReview = async (threadId) => {
        try {
            await reviewResolveMutation.mutateAsync({ manuscriptId, threadId });
            syncReviewThreadCommentResolved({
                currentDoc,
                editorView: codeMirrorRef.current?.view,
                threadId,
                resolved: true,
            });
            await reviewQuery.refetch();
            setToast("리뷰를 해결 상태로 변경했습니다.", "info");
            return true;
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || error.message || "리뷰 상태 변경에 실패했습니다.");
            return false;
        }
    };

    const handleReopenReview = async (threadId) => {
        try {
            await reviewReopenMutation.mutateAsync({ manuscriptId, threadId });
            syncReviewThreadCommentResolved({
                currentDoc,
                editorView: codeMirrorRef.current?.view,
                threadId,
                resolved: false,
            });
            await reviewQuery.refetch();
            setToast("리뷰를 다시 열었습니다.", "info");
            return true;
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || error.message || "리뷰 상태 변경에 실패했습니다.");
            return false;
        }
    };

    const registerReviewThreadAnchorElement = (threadId, element) => {
        if (!element) {
            reviewThreadAnchorElementMapRef.current.delete(threadId);
            return;
        }
        reviewThreadAnchorElementMapRef.current.set(threadId, element);
    };

    const closeReviewOverlay = () => {
        const closedState = createReviewOverlayClosedState();
        setSelectedReviewThreadId(closedState.selectedReviewThreadId);
        setReviewReplyDraft(closedState.reviewReplyDraft);
        setReviewReplyRequestAI(false);
        setReviewOverlayPosition(closedState.reviewOverlayPosition);
        setReviewComposerAnchor(null);
        setReviewComposerDraft("");
        setReviewComposerRequestAI(false);
        setPendingCreatedReviewThread(null);
    };

    const getSelectedReviewAnchorRect = () => {
        if (
            reviewComposerAnchor?.creation_anchor_range &&
            Number(reviewComposerAnchor.anchor_file_id) === Number(selectedTextNodeId)
        ) {
            const range = reviewComposerAnchor.creation_anchor_range;
            const view = codeMirrorRef.current?.view;
            if (view && range?.start_line) {
                const startLine = Math.min(Math.max(Number(range.start_line), 1), view.state.doc.lines);
                const endLine = Math.min(Math.max(Number(range.end_line || range.start_line), 1), view.state.doc.lines);
                const startLineInfo = view.state.doc.line(startLine);
                const endLineInfo = view.state.doc.line(endLine);
                const startPos = Math.min(
                    startLineInfo.to,
                    startLineInfo.from + Math.max(0, Number(range.start_col || 1) - 1),
                );
                const endPos = Math.min(
                    endLineInfo.to,
                    endLineInfo.from + Math.max(0, Number(range.end_col || range.start_col || 1) - 1),
                );
                const startCoords = view.coordsAtPos(startPos);
                const endCoords = view.coordsAtPos(endPos);
                if (startCoords) {
                    return {
                        top: startCoords.top,
                        bottom: Math.max(startCoords.bottom, endCoords?.bottom || startCoords.bottom),
                        left: startCoords.left,
                        right: Math.max(startCoords.right, endCoords?.right || startCoords.right),
                        source: "editor",
                    };
                }
            }
        }

        return getReviewThreadAnchorRect({
            selectedReviewThread,
            selectedTextNodeId,
            editorView: codeMirrorRef.current?.view,
            reviewThreadAnchorElementMap: reviewThreadAnchorElementMapRef.current,
            getReviewThreadRange,
        });
    };

    const updateReviewOverlayPosition = () => {
        const anchorRect = getSelectedReviewAnchorRect();
        if (!anchorRect) {
            setReviewOverlayPosition(null);
            return;
        }
        const nextPosition = buildReviewOverlayPosition({
            anchorRect,
            overlayRect: reviewOverlayRef.current?.getBoundingClientRect(),
        });
        if (!nextPosition) {
            setReviewOverlayPosition(null);
            return;
        }

        setReviewOverlayPosition((prev) => {
            if (prev && prev.top === nextPosition.top && prev.left === nextPosition.left) {
                return prev;
            }
            return nextPosition;
        });
    };
    updateReviewOverlayPositionRef.current = updateReviewOverlayPosition;

    const handleRestoreRevision = async (revisionId) => {
        try {
            await restoreMutation.mutateAsync({ manuscriptId, revisionId });
            setSelectedNodeId(null);
            await Promise.all([manuscriptQuery.refetch(), treeQuery.refetch(), historyQuery.refetch()]);
            setToast("히스토리를 복원했습니다.", "info");
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "히스토리 복원에 실패했습니다.");
        }
    };

    const closeSelectionActionMenu = () => {
        setSelectionActionMenuState(null);
    };

    const closeAIEditModal = useCallback(() => {
        if (isAIEditStreaming) {
            stopAIEditRequest();
        }
        resetAIEdit();
        setAIEditPrompt("");
        setAIEditLocalMessage("");
        setAIEditAttemptCount(0);
        setAIEditPreviewMode(false);
        setAIEditSession(null);
    }, [isAIEditStreaming, resetAIEdit, stopAIEditRequest]);

    const getCurrentEditorContent = useCallback(() => {
        if (typeof latestContentRef.current === "string" && latestContentRef.current) {
            return latestContentRef.current;
        }
        if (typeof currentDoc?.getSnapshot === "function") {
            return currentDoc.getSnapshot() || "";
        }
        return editorContent || "";
    }, [currentDoc, editorContent]);

    const closeLatexErrorFixModal = useCallback(() => {
        if (isLatexErrorFixRunning) {
            stopLatexErrorFixRequest();
        }
        resetLatexErrorFix();
        setLatexErrorFixLocalMessage("");
        setLatexErrorFixSession(null);
    }, [isLatexErrorFixRunning, resetLatexErrorFix, stopLatexErrorFixRequest]);

    const openLatexErrorFixModal = useCallback(
        ({ nodeId, line, diagnostics }) => {
            if (!requirePaidPlan("AI 문법 오류 수정")) {
                return;
            }
            if (!selectedNode || selectedNode.node_type !== "text" || Number(nodeId) !== Number(selectedNode.id)) {
                return;
            }

            const normalizedDiagnostics = (Array.isArray(diagnostics) ? diagnostics : [])
                .filter((diagnostic) => diagnostic?.source === "syntax")
                .map((diagnostic) => ({
                    line: diagnostic.line || line,
                    column: diagnostic.column,
                    severity: diagnostic.severity || "error",
                    message: diagnostic.message || "LaTeX 문법 오류",
                }));

            if (!normalizedDiagnostics.length) {
                return;
            }

            resetLatexErrorFix();
            setLatexErrorFixLocalMessage("");
            setLatexErrorFixSession({
                nodeId: selectedNode.id,
                nodeName: selectedNode.name,
                line,
                currentContent: getCurrentEditorContent(),
                diagnostics: normalizedDiagnostics,
            });
        },
        [getCurrentEditorContent, requirePaidPlan, resetLatexErrorFix, selectedNode],
    );

    const requestLatexErrorFix = () => {
        if (!requirePaidPlan("AI 문법 오류 수정")) {
            return;
        }
        if (!latexErrorFixSession) {
            return;
        }

        setLatexErrorFixLocalMessage("");
        startLatexErrorFixRequest({
            currentContent: latexErrorFixSession.currentContent,
            diagnostics: latexErrorFixSession.diagnostics,
        });
    };

    const applyLatexErrorFixResult = () => {
        if (!latexErrorFixSession) {
            return;
        }

        const editorView = codeMirrorRef.current?.view;
        if (!editorView) {
            setLatexErrorFixLocalMessage("에디터를 찾지 못해 결과를 반영할 수 없습니다.");
            return;
        }

        if (Number(selectedTextNodeId) !== Number(latexErrorFixSession.nodeId)) {
            setLatexErrorFixLocalMessage("선택된 파일이 변경되어 자동 반영할 수 없습니다. 다시 요청하세요.");
            return;
        }

        const currentContent = editorView.state.doc.toString();
        if (currentContent !== latexErrorFixSession.currentContent) {
            setLatexErrorFixLocalMessage("문서가 변경되어 자동 반영할 수 없습니다. 다시 요청하세요.");
            return;
        }

        const doc = editorView.state.doc;
        const replacements = Array.isArray(latexErrorFixReplacements) ? latexErrorFixReplacements : [];
        const changes = [];
        for (const replacement of replacements) {
            const startLine = Number(replacement?.start_line);
            const endLine = Number(replacement?.end_line || startLine);
            if (
                !Number.isInteger(startLine) ||
                !Number.isInteger(endLine) ||
                startLine < 1 ||
                endLine < startLine ||
                endLine > doc.lines
            ) {
                setLatexErrorFixLocalMessage("수정 제안의 줄 범위가 현재 문서와 맞지 않습니다. 다시 요청하세요.");
                return;
            }
            changes.push({
                from: doc.line(startLine).from,
                to: doc.line(endLine).to,
                insert: String(replacement?.replacement ?? ""),
            });
        }

        if (!changes.length) {
            setLatexErrorFixLocalMessage("반영할 수정 제안이 없습니다.");
            return;
        }

        changes.sort((a, b) => a.from - b.from);
        for (let index = 1; index < changes.length; index += 1) {
            if (changes[index - 1].to > changes[index].from) {
                setLatexErrorFixLocalMessage("수정 제안의 줄 범위가 서로 겹칩니다. 다시 요청하세요.");
                return;
            }
        }
        const firstChange = changes[0];

        editorView.dispatch({
            changes,
            selection: {
                anchor: firstChange.from + firstChange.insert.length,
            },
            scrollIntoView: true,
        });
        editorView.focus();
        setToast("LaTeX 오류 수정 결과를 반영했습니다.", "info");
        closeLatexErrorFixModal();
    };

    const openAIEditModal = ({ presetPrompt = "" } = {}) => {
        if (!requirePaidPlan("AI 편집")) {
            return;
        }
        const selectionFromMenu = selectionActionMenuState?.selection || selection || null;
        const editorView = codeMirrorRef.current?.view;

        if (!selectionFromMenu?.hasSelection || !editorView || !selectedNode || selectedNode.node_type !== "text") {
            closeSelectionActionMenu();
            Alert("warn", "에러", "AI 편집을 시작할 선택 영역을 찾지 못했습니다.");
            return;
        }

        const from = Math.min(Number(selectionFromMenu.from), Number(selectionFromMenu.to));
        const to = Math.max(Number(selectionFromMenu.from), Number(selectionFromMenu.to));
        const selectedText = editorView.state.sliceDoc(from, to);

        if (!Number.isFinite(from) || !Number.isFinite(to) || from === to || !selectedText) {
            closeSelectionActionMenu();
            Alert("warn", "에러", "AI 편집을 시작할 선택 영역이 유효하지 않습니다.");
            return;
        }

        resetAIEdit();
        setAIEditLocalMessage("");
        setAIEditAttemptCount(0);
        setAIEditPreviewMode(false);
        setAIEditPrompt(presetPrompt);
        setAIEditSession({
            nodeId: selectedNode.id,
            nodeName: selectedNode.name,
            documentVersion: currentVersionRef.current,
            currentContent: getCurrentEditorContent(),
            selection: {
                ...selectionFromMenu,
                from,
                to,
                selectedText,
            },
        });
        closeSelectionActionMenu();
    };

    const requestAIEdit = () => {
        if (!requirePaidPlan("AI 편집")) {
            return;
        }
        if (!aiEditSession) {
            return;
        }

        setAIEditLocalMessage("");
        setAIEditPreviewMode(false);
        const nextAttemptCount = aiEditAttemptCount + 1;
        setAIEditAttemptCount(nextAttemptCount);
        startAIEditRequest({
            currentContent: aiEditSession.currentContent,
            selectedText: aiEditSession.selection.selectedText,
            prompt: aiEditPrompt,
            selectionFrom: aiEditSession.selection.from,
            selectionTo: aiEditSession.selection.to,
            attemptCount: nextAttemptCount,
        });
    };

    const previewAIEditResult = () => {
        if (!aiEditHasResult || isAIEditStreaming) {
            return;
        }
        setAIEditPreviewMode(true);
    };

    const applyAIEditResult = () => {
        if (!aiEditSession) {
            return;
        }

        const editorView = codeMirrorRef.current?.view;
        if (!editorView) {
            setAIEditLocalMessage("에디터를 찾지 못해 결과를 반영할 수 없습니다.");
            return;
        }

        const from = Math.min(Number(aiEditSession.selection.from), Number(aiEditSession.selection.to));
        const to = Math.max(Number(aiEditSession.selection.from), Number(aiEditSession.selection.to));
        const currentSelectedText = editorView.state.sliceDoc(from, to);

        if (currentSelectedText !== aiEditSession.selection.selectedText) {
            setAIEditLocalMessage("문서가 변경되어 자동 반영할 수 없습니다. 다시 선택 후 요청하세요.");
            return;
        }

        const blockingError = getBlockingAIEditError({
            originalText: aiEditSession.selection.selectedText,
            replacementText: aiEditResponseText,
        });
        if (blockingError) {
            setAIEditLocalMessage(blockingError);
            return;
        }

        editorView.focus();
        editorView.dispatch({
            changes: {
                from,
                to,
                insert: aiEditResponseText,
            },
            selection: {
                anchor: from + aiEditResponseText.length,
                head: from + aiEditResponseText.length,
            },
            scrollIntoView: true,
        });
        setToast("AI 편집 결과를 반영했습니다.", "info");
        closeAIEditModal();
    };

    const copySelectionToClipboard = async (text) => {
        if (!text) {
            return false;
        }

        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }

        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "true");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textarea);
        return copied;
    };

    const selectionActionMenuItems = createSelectionActionMenuItems({
        onCopy: async () => {
            try {
                const selectedText = selectionActionMenuState?.selection?.selectedText || "";
                const copied = await copySelectionToClipboard(selectedText);
                if (!copied) {
                    throw new Error("선택 텍스트를 복사하지 못했습니다.");
                }
                closeSelectionActionMenu();
                setToast("선택 영역을 복사했습니다.", "info");
            } catch (error) {
                Alert("warn", "에러", error.message || "선택 영역 복사에 실패했습니다.");
            }
        },
        onCut: async () => {
            const selectionFromMenu = selectionActionMenuState?.selection || null;
            const editorView = codeMirrorRef.current?.view;

            if (!selectionFromMenu?.hasSelection || !editorView) {
                closeSelectionActionMenu();
                return;
            }

            const from = Math.min(Number(selectionFromMenu.from), Number(selectionFromMenu.to));
            const to = Math.max(Number(selectionFromMenu.from), Number(selectionFromMenu.to));

            if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) {
                closeSelectionActionMenu();
                return;
            }

            try {
                await copySelectionToClipboard(selectionFromMenu.selectedText || editorView.state.sliceDoc(from, to));
                editorView.focus();
                editorView.dispatch({
                    changes: { from, to, insert: "" },
                    selection: { anchor: from, head: from },
                    scrollIntoView: true,
                });
                closeSelectionActionMenu();
                setToast("선택 영역을 잘라냈습니다.", "info");
            } catch (error) {
                Alert("warn", "에러", error.message || "선택 영역 잘라내기에 실패했습니다.");
            }
        },
        onAddReview: () => {
            const selectionFromMenu = selectionActionMenuState?.selection || null;
            const nextReviewAnchor = resolveReviewAnchor({
                selectedNode,
                selection: selectionFromMenu || selection,
                editorView: codeMirrorRef.current?.view,
            });
            closeSelectionActionMenu();
            if (!nextReviewAnchor) {
                Alert("warn", "에러", "리뷰를 생성할 선택 범위를 찾지 못했습니다.");
                return;
            }
            setReviewReplyDraft("");
            setSelectedReviewThreadId(null);
            setReviewComposerAnchor(nextReviewAnchor);
            setRightPanelTab("review");
            setIsRightPanelCollapsed(false);
        },
        onAIEdit: () => {
            openAIEditModal();
        },
    });

    const aiEditLocationLabel = aiEditSession ? formatSelectionLocation(aiEditSession.selection) : "";
    const aiEditStatusMessage =
        aiEditLocalMessage ||
        (aiEditStatus === "streaming"
            ? "AI 응답을 생성 중입니다."
            : aiEditStatus === "completed"
              ? "응답 생성을 마쳤습니다. 반영하거나 다시 요청할 수 있습니다."
              : aiEditStatus === "stopped"
                ? "요청이 중지되었습니다. 응답을 검토하거나 다시 요청할 수 있습니다."
                : "");
    const latexErrorFixStatusMessage =
        latexErrorFixLocalMessage ||
        (latexErrorFixStatus === "running"
            ? "LaTeX 오류 수정안을 생성 중입니다."
            : latexErrorFixStatus === "completed"
              ? "수정안 생성을 마쳤습니다. diff를 확인한 뒤 반영할 수 있습니다."
              : latexErrorFixStatus === "stopped"
                ? "요청이 중지되었습니다. 생성된 내용이 있으면 검토할 수 있습니다."
                : "");

    const handleOpenSelectionActionMenu = (payload) => {
        if (!payload) {
            setSelectionActionMenuState(null);
            return;
        }

        const nextState = createSelectionActionMenuState({
            payload,
            enabled: selectedNode?.node_type === "text",
        });
        if (!nextState) {
            setSelectionActionMenuState(null);
            return;
        }
        setSelection(nextState.selection);
        setSelectionActionMenuState(nextState);
    };

    const dispatchEditorCommand = (type) => {
        setEditorCommand({
            type,
            key: `${type}-${Date.now()}`,
        });
    };

    const {
        closeHeaderMenus,
        closeHelpMenu,
        headerMenuItems,
        headerMenuRef,
        headerMenuState,
        helpMenuPosition,
        helpMenuRef,
        openHeaderMenu,
        openHelpMenu,
        runHeaderMenuAction,
    } = useManuscriptHeaderMenus({
        createNode,
        dispatchEditorCommand,
        downloadSourceZip: handleDownloadSourceZip,
        fileInputRef,
        flushCurrentDocument,
        isMac,
        isRightPanelCollapsed,
        setIsRightPanelCollapsed,
        setRightPanelTab,
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!treeActionMenuRef.current?.contains(event.target)) {
                setTreeActionMenuNodeId(null);
                setTreeActionMenuPosition(null);
            }
            if (!editorReviewContextMenuRef.current?.contains(event.target)) {
                closeSelectionActionMenu();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key !== "Escape") return;
            if (aiEditSession) {
                closeAIEditModal();
                return;
            }
            if (latexErrorFixSession) {
                closeLatexErrorFixModal();
                return;
            }
            if (isShortcutModalOpen) {
                setIsShortcutModalOpen(false);
                return;
            }
            if (isSettingsModalOpen) {
                setIsSettingsModalOpen(false);
                return;
            }
            closeHeaderMenus();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [
        aiEditSession,
        closeAIEditModal,
        closeHeaderMenus,
        closeLatexErrorFixModal,
        isShortcutModalOpen,
        isSettingsModalOpen,
        latexErrorFixSession,
    ]);

    const moveFileNodeWithConflict = async (node, parentId) => {
        let nextName = node.name;
        const existingFile = findNodeConflict(parentId, nextName, node.node_type, node.id);
        if (existingFile) {
            const action = await resolveFileConflict({
                name: nextName,
                parentId,
                operation: "move",
            });
            if (action === "cancel") {
                return { moved: false, cancelled: true };
            }
            if (action === "skip") {
                return { moved: false };
            }
            if (action === "rename") {
                nextName = createAutoRenamedFileName(nextName, parentId, node.node_type, node.id);
            }
            if (action === "overwrite") {
                await nodeMutation.mutateAsync({
                    type: "delete",
                    nodeId: existingFile.id,
                });
            }
        }

        await nodeMutation.mutateAsync({
            type: "update",
            nodeId: node.id,
            payload: { parent_id: parentId, name: nextName },
        });
        reserveFileConflictName(parentId, node.node_type, nextName);
        return { moved: true };
    };

    const moveNodeWithConflict = async (node, parentId) => {
        if (!node) return { moved: false };
        if (node.node_type !== "folder") {
            if (
                String(parentId || "") === String(node.parent_id || "") &&
                !findNodeConflict(parentId, node.name, node.node_type, node.id)
            ) {
                return { moved: false };
            }
            return moveFileNodeWithConflict(node, parentId);
        }

        const existingFolder = findNodeConflict(parentId, node.name, "folder", node.id);
        if (!existingFolder) {
            if (String(parentId || "") === String(node.parent_id || "")) {
                return { moved: false };
            }
            await nodeMutation.mutateAsync({
                type: "update",
                nodeId: node.id,
                payload: { parent_id: parentId },
            });
            return { moved: true };
        }

        const children = treeNodes.filter((child) => Number(child.parent_id) === Number(node.id));
        let movedAny = false;
        let allMoved = true;
        for (const child of children) {
            const result = await moveNodeWithConflict(child, existingFolder.id);
            movedAny = movedAny || !!result?.moved;
            if (result?.cancelled) {
                return { moved: movedAny, cancelled: true };
            }
            if (!result?.moved) {
                allMoved = false;
            }
        }

        if (allMoved) {
            await nodeMutation.mutateAsync({
                type: "delete",
                nodeId: node.id,
            });
            return { moved: true };
        }

        return { moved: movedAny };
    };

    const moveNodeToParent = async (node, parentNode) => {
        if (!node) return;
        const parentId = parentNode?.node_type === "folder" ? parentNode.id : parentNode?.parent_id || null;
        resetFileConflictBatch();
        try {
            const result = await moveNodeWithConflict(node, parentId);
            if (result?.cancelled) {
                return;
            }
            if (result?.moved) {
                setToast("파일 위치를 이동했습니다.", "info");
            }
        } catch (error) {
            Alert(
                "warn",
                "에러",
                error.response?.data?.detail ||
                    error.response?.data?.name?.[0] ||
                    error.response?.data?.parent_id?.[0] ||
                    "파일 이동에 실패했습니다.",
            );
        }
    };

    const runManuscriptSearch = async () => {
        const term = searchQuery.trim();
        if (!term) {
            setSearchResults([]);
            return;
        }

        const runId = Date.now();
        searchRunRef.current = runId;
        setSearchLoading(true);
        try {
            const results = await getManuscriptSearch(space_uuid, manuscriptId, {
                q: term,
                limit: 1000,
            });
            if (searchRunRef.current !== runId) return;
            setSearchResults(results || []);
            setSearchLoading(false);
        } catch (_error) {
            if (searchRunRef.current !== runId) return;
            setSearchResults([]);
            setSearchLoading(false);
        }
    };

    const handleSearchResultSelect = async (result) => {
        const nodeId = result.nodeId ?? result.node_id;
        const node = nodeMap.get(Number(nodeId));
        if (!node) return;
        await openNode(node);
        if (result.line) {
            window.setTimeout(() => {
                jumpToLine(result.line, {
                    startCol: result.startCol ?? result.start_col,
                    endCol: result.endCol ?? result.end_col,
                });
            }, 60);
        }
    };

    const handleCompileLogEntrySelect = async (entry) => {
        const normalizedPath = normalizeCompileLogPath(entry?.file || "");
        const lineNumber = Number.parseInt(entry?.line, 10);

        if (!normalizedPath || !Number.isFinite(lineNumber)) {
            return;
        }

        const matchedNode =
            treeNodes.find((node) => {
                if (node.node_type !== "text") return false;
                const nodePath = normalizeCompileLogPath(node.path_cache || "");
                return nodePath === normalizedPath;
            }) ||
            treeNodes.find((node) => {
                if (node.node_type !== "text") return false;
                const nodePath = normalizeCompileLogPath(node.path_cache || "");
                return nodePath.endsWith(`/${normalizedPath}`) || normalizedPath.endsWith(`/${nodePath}`);
            });

        if (!matchedNode) {
            setToast(`로그 항목의 파일을 찾을 수 없습니다: ${normalizedPath}`, "warning");
            return;
        }

        setRightPanelTab("pdf");
        setIsRightPanelCollapsed(false);
        await openNode(matchedNode);
        setEditorCommand({
            type: "focusLine",
            lineNumber,
            key: `focusLine-${matchedNode.id}-${lineNumber}-${Date.now()}`,
        });
    };

    const handleLeftPanelTabSelect = (panel) => {
        if (panel === leftPanelTab && !spaceMenuFoldRef.current?.isFold) {
            spaceMenuFoldRef.current?.setFold(true);
            return;
        }
        setLeftPanelTab(panel);
        spaceMenuFoldRef.current?.setFold(false);
    };

    const leftRailTopContent = (
        <>
            <LeftRailNavButton
                type="button"
                aria-label="파일"
                $active={leftPanelTab === "files"}
                onClick={() => handleLeftPanelTabSelect("files")}
            >
                <Icon
                    name={"folder"}
                    size={"16px"}
                    color={leftPanelTab === "files" ? "var(--color-Key)" : "var(--color-Grey1)"}
                />
            </LeftRailNavButton>
            <LeftRailNavButton
                type="button"
                aria-label="검색"
                $active={leftPanelTab === "search"}
                onClick={() => handleLeftPanelTabSelect("search")}
            >
                <Icon
                    name={"search"}
                    size={"16px"}
                    color={leftPanelTab === "search" ? "var(--color-Key)" : "var(--color-Grey1)"}
                />
            </LeftRailNavButton>
        </>
    );

    const leftRailBottomContent = (
        <>
            <LeftRailNavButton
                type="button"
                aria-label="설정"
                onClick={() => {
                    closeHelpMenu();
                    setIsShortcutModalOpen(false);
                    setIsSettingsModalOpen(true);
                }}
            >
                <Icon name={"option"} size={"16px"} color={"var(--color-Grey1)"} />
            </LeftRailNavButton>
            <LeftRailNavButton
                type="button"
                aria-label="도움말"
                onClick={(event) => {
                    setIsShortcutModalOpen(false);
                    openHelpMenu(event.currentTarget, "right");
                }}
            >
                <Icon name={"question"} size={"16px"} color={"var(--color-Grey1)"} />
            </LeftRailNavButton>
        </>
    );

    return (
        <PageLayout>
            <HtmlHead title={manuscriptDisplayName} />
            <ManuscriptEditorPageHeader
                activeMenuKey={headerMenuState?.key}
                headerMenuItems={headerMenuItems}
                headerMenuRef={headerMenuRef}
                headerMenuState={headerMenuState}
                helpMenuPosition={helpMenuPosition}
                helpMenuRef={helpMenuRef}
                isConnected={isConnected}
                isTitleEditing={isManuscriptTitleEditing}
                manuscriptDisplayName={manuscriptDisplayName}
                manuscriptTitleDraft={manuscriptTitleDraft}
                onHelpMenuOpen={(anchorElement) => {
                    setIsShortcutModalOpen(false);
                    openHelpMenu(anchorElement, "bottom");
                }}
                onHeaderMenuAction={runHeaderMenuAction}
                onHeaderMenuOpen={openHeaderMenu}
                onShortcutOpen={() => {
                    closeHelpMenu();
                    setIsShortcutModalOpen(true);
                }}
                onTitleBlur={handleManuscriptTitleBlur}
                onTitleChange={setManuscriptTitleDraft}
                onTitleFocus={() => setIsManuscriptTitleEditing(true)}
                onTitleKeyDown={handleManuscriptTitleKeyDown}
                presenceParticipants={activePresenceParticipants}
                titleInputRef={manuscriptTitleInputRef}
            />
            <SpaceDetailPageLayout
                headerHeight={HEADER_HEIGHT}
                mainBackgroundColor="var(--color-Base1)"
                navigate={navigate}
                onBack={() => navigate(`/${space_uuid}/manuscript`)}
                spaceBarMode="manuscript-detail"
                spaceBarTopContent={leftRailTopContent}
                spaceBarBottomContent={leftRailBottomContent}
                sidebarTitle={manuscriptDisplayName}
                sidebarTitleLabel="논문원고"
                sidebarContent={
                    <ManuscriptLeftPanel
                        activePanel={leftPanelTab}
                        roots={roots}
                        expandedFolderIds={expandedFolderIds}
                        selectedNodeId={selectedNodeId}
                        treeActionMenuNodeId={treeActionMenuNodeId}
                        treeActionMenuRef={treeActionMenuRef}
                        createNode={createNode}
                        fileInputRef={fileInputRef}
                        uploadBinary={uploadBinary}
                        onOpenNode={openNode}
                        onMoveNodeToParent={moveNodeToParent}
                        onTreeActionMenuOpen={(nodeId, position) => {
                            setTreeActionMenuPosition(position);
                            setTreeActionMenuNodeId((prev) => (prev === nodeId ? null : nodeId));
                        }}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        onSearchSubmit={runManuscriptSearch}
                        searchLoading={searchLoading}
                        searchResults={searchResults}
                        onSearchResultSelect={handleSearchResultSelect}
                        outlineItems={documentOutline}
                        outlineEnabled={selectedNode?.node_type === "text"}
                        activeOutlineLine={activeEditorLine}
                        activeOutlineIds={activeOutlineIds}
                        onOutlineSelect={jumpToLine}
                    />
                }
                spaceUuid={space_uuid}
                user={userQuery.data}
                spaceMenuFoldRef={spaceMenuFoldRef}
            >
                <ManuscriptEditorPageLayout
                    main={
                        <ManuscriptEditorPanel
                            spaceUuid={space_uuid}
                            manuscriptId={manuscriptId}
                            selectedNode={selectedNode}
                            manuscriptTreeData={treeNodes}
                            setToast={setToast}
                            uploadBinaryFile={uploadBinaryFile}
                            importFigureUrl={importFigureUrl}
                            saveLabel={saveLabel}
                            onSave={flushCurrentDocument}
                            saveDisabled={!selectedTextNodeId || templateGenerationInProgress}
                            selection={selection}
                            editorContent={editorContent}
                            onEditorChange={onEditorChange}
                            onSelectionChange={updateSelectionInfo}
                            onActiveLineChange={updateActiveEditorLine}
                            editorRef={codeMirrorRef}
                            currentVersion={currentVersion}
                            currentEditorMode={currentEditorMode}
                            availableEditorModes={documentQuery.data?.available_editor_modes || ["code"]}
                            onEditorModeChange={handleEditorModeChange}
                            currentLocationPath={activeOutlinePath}
                            onLocationSelect={jumpToLine}
                            onOpenSelectionActionMenu={handleOpenSelectionActionMenu}
                            onLatexSyntaxErrorClick={openLatexErrorFixModal}
                            onSelectReviewThread={handleSelectReviewThread}
                            externalCommand={editorCommand}
                            realtimeExtension={realtimeExtension}
                            remoteCursors={activeRemoteCursors}
                            compileLogAnnotations={compileLogAnnotationsByNodeId[selectedTextNodeId] || []}
                            compiling={compileInProgress}
                            editedSinceCompileStarted={isDirty}
                            syntaxValidation={true}
                            readOnly={templateGenerationInProgress}
                            requirePaidPlan={requirePaidPlan}
                        />
                    }
                    sidebar={
                        <ManuscriptRightPanel
                            activePanel={rightPanelTab}
                            latestCompile={latestCompile}
                            pdfArtifact={displayedPdfArtifact}
                            pdfRevisionSeq={displayedPdfRevisionSeq}
                            logArtifact={logArtifact}
                            compileRevisionSeq={latestCompileRevisionSeq}
                            compileLogRaw={compileLogRaw}
                            compileLogEntries={compileLogEntries}
                            compileLogLoading={compileLogLoading}
                            compileLogLoadError={compileLogLoadError}
                            onCompile={handleCompileAction}
                            onLogEntrySelect={handleCompileLogEntrySelect}
                            compileLoading={compileMutation.isLoading}
                            compileDisabled={!selectedTextNodeId}
                            compileHasLintError={hasLintingError}
                            compileDisabledReason={compileDisabledReason}
                            historyItems={historyItems}
                            highlightedRevisionId={highlightedRevisionId}
                            restoreMutation={restoreMutation}
                            handleRestoreRevision={handleRestoreRevision}
                            saveLabel={saveLabel}
                            reviewThreads={reviewThreads}
                            selectedReviewThreadId={selectedReviewThreadId}
                            onSelectReviewThread={handleSelectReviewThread}
                            onReviewThreadMount={registerReviewThreadAnchorElement}
                        />
                    }
                    sidebarRail={
                        <ManuscriptRightRail
                            activePanel={rightPanelTab}
                            isCollapsed={isRightPanelCollapsed}
                            onToggle={() => setIsRightPanelCollapsed((prev) => !prev)}
                            onSelect={(panel) => {
                                if (panel === rightPanelTab && !isRightPanelCollapsed) {
                                    setIsRightPanelCollapsed(true);
                                    return;
                                }
                                setRightPanelTab(panel);
                                setIsRightPanelCollapsed(false);
                            }}
                        />
                    }
                    sidebarWidth={rightPanelWidth}
                    onSidebarWidthChange={setRightPanelWidth}
                    isSidebarCollapsed={isRightPanelCollapsed}
                    onSidebarCollapsedChange={setIsRightPanelCollapsed}
                />
            </SpaceDetailPageLayout>
            {treeActionMenuNodeId && treeActionMenuPosition
                ? createPortal(
                      <TreeActionMenu
                          ref={treeActionMenuRef}
                          $top={treeActionMenuPosition.top}
                          $left={treeActionMenuPosition.left}
                      >
                          <TreeActionMenuItem
                              type="button"
                              onClick={() => {
                                  const node = nodeMap.get(Number(treeActionMenuNodeId));
                                  setTreeActionMenuNodeId(null);
                                  setTreeActionMenuPosition(null);
                                  renameSpecificNode(node);
                              }}
                          >
                              이름 바꾸기
                          </TreeActionMenuItem>
                          {nodeMap.get(Number(treeActionMenuNodeId))?.node_type === "text" &&
                          nodeMap.get(Number(treeActionMenuNodeId))?.name?.toLowerCase().endsWith(".tex") ? (
                              <TreeActionMenuItem
                                  type="button"
                                  onClick={() => {
                                      const node = nodeMap.get(Number(treeActionMenuNodeId));
                                      setTreeActionMenuNodeId(null);
                                      setTreeActionMenuPosition(null);
                                      setRootNode(node);
                                  }}
                              >
                                  루트로 지정
                              </TreeActionMenuItem>
                          ) : null}
                          <TreeActionMenuItem
                              type="button"
                              onClick={() => {
                                  const node = nodeMap.get(Number(treeActionMenuNodeId));
                                  setTreeActionMenuNodeId(null);
                                  setTreeActionMenuPosition(null);
                                  removeSpecificNode(node);
                              }}
                          >
                              삭제
                          </TreeActionMenuItem>
                      </TreeActionMenu>,
                      document.body,
                  )
                : null}
            <ManuscriptReviewThreadOverlay
                thread={selectedReviewThread}
                composerAnchor={reviewComposerAnchor}
                position={reviewOverlayPosition}
                focusedMessageId={focusedReviewMessageId}
                overlayRef={reviewOverlayRef}
                replyDraft={reviewReplyDraft}
                replyRequestAI={reviewReplyRequestAI}
                composerDraft={reviewComposerDraft}
                mentions={{ enabled: true, data: reviewMentionCandidates }}
                requestAIReview={reviewComposerRequestAI}
                onReplyDraftChange={setReviewReplyDraft}
                onReplyRequestAIChange={(checked) => {
                    if (checked && !requirePaidPlan("AI 리뷰")) {
                        return;
                    }
                    setReviewReplyRequestAI(checked);
                }}
                onComposerDraftChange={setReviewComposerDraft}
                onRequestAIReviewChange={(checked) => {
                    if (checked && !requirePaidPlan("AI 리뷰")) {
                        return;
                    }
                    setReviewComposerRequestAI(checked);
                }}
                onCreateReview={handleCreateReview}
                onReplyReview={handleReplyReview}
                onResolveReview={handleResolveReview}
                onReopenReview={handleReopenReview}
                onClose={closeReviewOverlay}
                onBeforeReply={() => {
                    suppressReviewOverlayDismissRef.current = true;
                }}
                onBeforeReopen={() => {
                    suppressReviewOverlayDismissRef.current = true;
                }}
                onReplySuccess={() => {
                    setReviewReplyDraft("");
                }}
                replyLoading={reviewMessageMutation.isLoading}
                createLoading={reviewCreateMutation.isLoading}
                resolveLoading={reviewResolveMutation.isLoading}
                reopenLoading={reviewReopenMutation.isLoading}
            />
            <ManuscriptAiEditModal
                session={aiEditSession}
                locationLabel={aiEditLocationLabel}
                prompt={aiEditPrompt}
                responseText={aiEditResponseText}
                isStreaming={isAIEditStreaming}
                hasResult={aiEditHasResult}
                previewMode={aiEditPreviewMode}
                errorMessage={aiEditErrorMessage}
                statusMessage={aiEditStatusMessage}
                onPromptChange={setAIEditPrompt}
                onRequest={requestAIEdit}
                onStop={stopAIEditRequest}
                onApply={previewAIEditResult}
                onPreviewBack={() => setAIEditPreviewMode(false)}
                onConfirmApply={applyAIEditResult}
                onClose={closeAIEditModal}
            />
            <ManuscriptLatexErrorFixModal
                open={Boolean(latexErrorFixSession)}
                session={latexErrorFixSession}
                summary={latexErrorFixSummary}
                replacements={latexErrorFixReplacements}
                isRunning={isLatexErrorFixRunning}
                hasResult={latexErrorFixHasResult}
                errorMessage={latexErrorFixErrorMessage}
                statusMessage={latexErrorFixStatusMessage}
                onRequest={requestLatexErrorFix}
                onStop={stopLatexErrorFixRequest}
                onApply={applyLatexErrorFixResult}
                onClose={closeLatexErrorFixModal}
            />
            {nodeNameModal ? (
                <ManuscriptNodeNameModal
                    title={nodeNameModal.title}
                    initialName={nodeNameModal.initialName}
                    submitText={nodeNameModal.submitText}
                    hint={nodeNameModal.hint}
                    exitModal={() => setNodeNameModal(null)}
                    onSubmit={(name) => {
                        if (nodeNameModal.mode === "create") {
                            createNodeWithName(nodeNameModal.nodeType, name);
                            return;
                        }
                        if (nodeNameModal.mode === "rename") {
                            renameNodeWithName(nodeNameModal.node, name);
                        }
                    }}
                />
            ) : null}
            {selectionActionMenuState
                ? createPortal(
                      <EditorContextMenu
                          ref={editorReviewContextMenuRef}
                          style={{
                              top: selectionActionMenuState.position.top,
                              left: selectionActionMenuState.position.left,
                          }}
                      >
                          {selectionActionMenuItems.map((item, index) => {
                              const previousItem = selectionActionMenuItems[index - 1] || null;
                              const showDivider = previousItem && previousItem.group !== item.group;

                              return (
                                  <React.Fragment key={item.id}>
                                      {showDivider ? <EditorContextMenuDivider /> : null}
                                      <EditorContextMenuTooltipWrap>
                                          <EditorContextMenuItem
                                              type="button"
                                              aria-label={item.label}
                                              onClick={item.onSelect}
                                          >
                                              <Icon name={item.icon} size={"14px"} color={item.iconColor} />
                                          </EditorContextMenuItem>
                                          <ContextMenuToolTip>{item.label}</ContextMenuToolTip>
                                      </EditorContextMenuTooltipWrap>
                                  </React.Fragment>
                              );
                          })}
                      </EditorContextMenu>,
                      document.body,
                  )
                : null}
            <ManuscriptTemplateOverlay job={templateGenerationJob} />
            {fileConflictDialog ? (
                <Overlay>
                    <Dialog
                        role="dialog"
                        aria-label="파일 충돌"
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <DialogHeader>
                            <div>파일 충돌</div>
                        </DialogHeader>
                        <DialogBody>
                            <ConflictMessage>
                                <div>
                                    대상 위치에 같은 이름의 파일 <strong>{fileConflictDialog.name}</strong>이 이미
                                    있습니다.
                                </div>
                                <div>이 파일을 어떻게 처리할까요?</div>
                                <ConflictApplyRow>
                                    <input
                                        type="checkbox"
                                        checked={fileConflictApplyAll}
                                        onChange={(event) => setFileConflictApplyAll(event.target.checked)}
                                    />
                                    이 선택을 남은 파일 충돌에 모두 적용
                                </ConflictApplyRow>
                            </ConflictMessage>
                            <Actions>
                                <ActionButton
                                    type="button"
                                    $primary
                                    onClick={() => chooseFileConflictAction("overwrite")}
                                >
                                    덮어쓰기
                                </ActionButton>
                                <ActionButton type="button" onClick={() => chooseFileConflictAction("rename")}>
                                    이름변경
                                </ActionButton>
                                <ActionButton type="button" onClick={() => chooseFileConflictAction("skip")}>
                                    건너뛰기
                                </ActionButton>
                                <ActionButton type="button" onClick={cancelFileConflictDialog}>
                                    취소
                                </ActionButton>
                            </Actions>
                        </DialogBody>
                    </Dialog>
                </Overlay>
            ) : null}
            {isShortcutModalOpen ? (
                <ManuscriptShortcutModal
                    ActionButton={ActionButton}
                    Dialog={Dialog}
                    DialogBody={DialogBody}
                    DialogHeader={DialogHeader}
                    onClose={() => {
                        setIsShortcutModalOpen(false);
                    }}
                    Overlay={Overlay}
                />
            ) : null}
            {isSettingsModalOpen ? (
                <ManuscriptSettingsModal
                    ActionButton={ActionButton}
                    compilerOptions={compilerOptions}
                    Dialog={Dialog}
                    DialogBody={DialogBody}
                    DialogHeader={DialogHeader}
                    isSaving={nodeMutation.isLoading || manuscriptSettingsMutation.isLoading}
                    onClose={() => {
                        setIsSettingsModalOpen(false);
                    }}
                    onCompilerChange={(event) => setSettingsCompiler(event.target.value)}
                    onRootNodeChange={(event) => setSettingsRootNodeId(event.target.value)}
                    onSave={saveProjectSettings}
                    onSettingsTabChange={setSettingsTab}
                    Overlay={Overlay}
                    rootCandidateNodes={rootCandidateNodes}
                    settingsCompiler={settingsCompiler}
                    settingsRootNodeId={settingsRootNodeId}
                    settingsTab={settingsTab}
                />
            ) : null}
        </PageLayout>
    );
}

export default ManuscriptEditorPage;
